import React, { useState, useEffect, useRef, useCallback } from 'react';
import * as XLSX from 'xlsx';
import {
  getTasks,
  createTask,
  bulkCreateTasks,
  updateTask,
  deleteTask,
  getSOVLines,
  getTaskAllocations,
  createAllocation,
  // Schedule-tab uses schedule_sections (project's WORK breakdown).
  // Distinct from SOV's `sections` table (project's COST breakdown).
  // Even when names happen to overlap (e.g. both have "Building A"),
  // they're separate rows in separate tables and don't cross-leak.
  getScheduleSections,
  createScheduleSection,
  deleteScheduleSection,
  reorderScheduleSections,
  addPredecessor,
  updatePredecessor,
  removePredecessor,
  getProgressAlertsSummary,
  getDependencyAlerts
} from '../api';
import ConfirmDialog from './ConfirmDialog';
import LoadingState from './LoadingState';
import TaskSOVLinks from './TaskSOVLinks';
import useConfirm from '../hooks/useConfirm';
import { useTheme } from '../hooks/useTheme';
import { Icon, LoadingDialog, Popover, PopoverHeader, PopoverDivider, CheckMenuItem, RadioMenuItem } from './ui';
import { SCHEDULE_SORT_OPTIONS, SCHEDULE_TASK_TYPES } from './scheduleSort';
import '../App.css';

/* -------------------- toolbar helpers --------------------
 * Tiny ghost button (Filter / Sort) and square icon-only button used
 * in the redesigned Schedule action bar. Lifted to the module scope
 * so they don't re-render on every TasksTab tick.
 */
function GhostButton({ icon, label, active, onClick }) {
  const [hover, setHover] = useState(false);
  const bg = active ? 'var(--bg-surface)' : hover ? 'var(--bg-hover)' : 'transparent';
  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 5,
        height: 28, padding: '0 10px',
        border: `1px solid ${active ? 'var(--border-default)' : 'transparent'}`,
        background: bg,
        color: active ? 'var(--text-1)' : 'var(--text-3)',
        fontSize: 13.5, fontWeight: 500, cursor: 'pointer', borderRadius: 6,
        boxShadow: active ? 'var(--shadow-xs)' : 'none',
        transition: 'background .12s, color .12s, border-color .12s',
        whiteSpace: 'nowrap',
      }}
    >
      <Icon name={icon} size={13} />
      {label}
    </button>
  );
}

function IconUtilBtn({ name, label, onClick }) {
  const [hover, setHover] = useState(false);
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        width: 28, height: 28, border: 'none', borderRadius: 6,
        background: hover ? 'var(--bg-hover)' : 'transparent',
        color: hover ? 'var(--text-1)' : 'var(--text-3)',
        cursor: 'pointer', display: 'inline-flex',
        alignItems: 'center', justifyContent: 'center', padding: 0,
        transition: 'background .12s, color .12s',
      }}
    >
      <Icon name={name} size={14} />
    </button>
  );
}

// Helper function to format dates as MM-DD-YYYY
const fmtDate = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr + 'T00:00:00');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const year = date.getFullYear();
  return `${month}-${day}-${year}`;
};

// Task type color mapping matching Base44 design
const getTaskTypeStyle = (taskType) => {
  switch ((taskType || '').toLowerCase()) {
    case 'material':
    case 'procurement':
      return { backgroundColor: '#FFF3E0', color: '#E65100', border: '1px solid #FFB74D' };
    case 'labor':
    case 'work':
      return { backgroundColor: '#E3F2FD', color: '#1565C0', border: '1px solid #64B5F6' };
    case 'engineering':
      return { backgroundColor: '#E8F5E9', color: '#2E7D32', border: '1px solid #81C784' };
    case 'inspection':
      return { backgroundColor: '#F3E5F5', color: '#6A1B9A', border: '1px solid #BA68C8' };
    case 'milestone':
      return { backgroundColor: '#FFF8E1', color: '#F57F17', border: '1px solid #FFD54F' };
    default:
      return { backgroundColor: '#F5F5F5', color: '#616161', border: '1px solid #BDBDBD' };
  }
};

/**
 * Schedule-redesign: map task_type → CSS custom property holding the
 * type color. Used for the 7px leading dot + Gantt bar fills. The
 * underlying tokens (engineering sage / procurement amber / labor blue
 * / inspection purple / milestone gold) live in styles/tokens.css and
 * flip between light + dark via .app.dark.
 */
const getTaskTypeColorVar = (taskType) => {
  switch ((taskType || '').toLowerCase()) {
    case 'material':
    case 'procurement':
      return 'var(--type-procurement)';
    case 'labor':
    case 'work':
      return 'var(--type-labor)';
    case 'engineering':
      return 'var(--type-engineering)';
    case 'inspection':
      return 'var(--type-inspection)';
    case 'milestone':
      return 'var(--type-milestone)';
    default:
      return 'var(--text-3)';
  }
};

/** Compact date "May 13" / "Jun 4". Used in the refined dates cell so
 *  the row stays under one line at the redesign's tighter spacing. */
const fmtCompactDate = (s) => {
  if (!s) return '';
  const d = new Date(s);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

/** Stable hash → OKLCH hue for a section name. Mirror of the helper
 *  in GanttTab.js — kept in lockstep so the left section header dot
 *  matches the right panel's roll-up bar color. */
const sectionHue = (name) => {
  let h = 0;
  const s = name || '';
  for (let i = 0; i < s.length; i++) {
    h = (h * 31 + s.charCodeAt(i)) >>> 0;
  }
  return h % 360;
};
const sectionColorFor = (name) => `oklch(0.55 0.1 ${sectionHue(name)})`;


// Status badge style
const getStatusStyle = (status) => {
  switch ((status || '').toLowerCase()) {
    case 'not started':
      return { backgroundColor: '#F5F5F5', color: '#757575', border: '1px solid #E0E0E0' };
    case 'in progress':
      return { backgroundColor: '#E3F2FD', color: '#1565C0', border: '1px solid #64B5F6' };
    case 'complete':
      return { backgroundColor: '#E8F5E9', color: '#2E7D32', border: '1px solid #81C784' };
    default:
      return { backgroundColor: '#F5F5F5', color: '#757575', border: '1px solid #E0E0E0' };
  }
};

// Generate section prefix from building_area_phase (e.g., "Elementary School" -> "ES")
const getSectionPrefix = (section) => {
  if (!section || section === 'Unsectioned') return 'T';
  const words = section.trim().split(/\s+/);
  if (words.length === 1) return words[0].substring(0, 2).toUpperCase();
  return words.map(w => w[0]).join('').toUpperCase();
};

// Generate task code from section prefix + index (1-based, 3-digit padded)
const generateTaskCode = (section, index) => {
  const prefix = getSectionPrefix(section);
  return `${prefix}-${String(index).padStart(3, '0')}`;
};

const DEP_TYPES = [
  { value: 'FS', label: 'FS — Finish to Start', short: 'FS' },
  { value: 'SS', label: 'SS — Start to Start', short: 'SS' },
  { value: 'FF', label: 'FF — Finish to Finish', short: 'FF' },
  { value: 'SF', label: 'SF — Start to Finish', short: 'SF' },
];

// Predecessor section component matching Base44 design
// Supports two modes:
//   "api" mode (default): saves/removes via addPredecessor/removePredecessor API (for Task Properties)
//   "local" mode: manages predecessors in local state, returns them via onLocalChange (for Add Task modal)
const PredecessorSection = ({ currentTask, tasks, editingTaskId, inputStyle, fieldGroup, addPredecessor, updatePredecessor, removePredecessor, fetchTasks, confirm, mode = 'api', localPredecessors = [], onLocalChange }) => {
  const t = useTheme();
  const [searchText, setSearchText] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedPredTask, setSelectedPredTask] = useState(null);
  const [depType, setDepType] = useState('FS');
  const [lagDays, setLagDays] = useState(0);

  const isLocal = mode === 'local';
  const currentPredecessors = isLocal
    ? localPredecessors
    : (currentTask?.predecessors || []).filter(p => p.predecessor_task_id != null);
  const alreadyLinkedIds = new Set(currentPredecessors.map(p => Number(p.predecessor_task_id)));

  // Available tasks: exclude self and already-linked
  const availableTasks = tasks
    .filter(t => t.id !== editingTaskId && !alreadyLinkedIds.has(Number(t.id)))
    .sort((a, b) => (a.task_code || '').localeCompare(b.task_code || '', undefined, { numeric: true }));

  // Filter by search text
  const filteredTasks = searchText.trim()
    ? availableTasks.filter(t => {
        const q = searchText.toLowerCase();
        return (t.task_code || '').toLowerCase().includes(q) ||
               (t.task_name || '').toLowerCase().includes(q) ||
               (t.building_area_phase || '').toLowerCase().includes(q);
      })
    : availableTasks;

  const handleSelectTask = async (task) => {
    if (isLocal) {
      // In local mode (Add Task), immediately add with defaults
      const newPred = {
        id: `local_${Date.now()}`,
        predecessor_task_id: task.id,
        dependency_type: 'FS',
        lag_days: 0,
      };
      onLocalChange([...localPredecessors, newPred]);
      setSearchText('');
      setShowDropdown(false);
    } else {
      // In API mode (Edit Task), immediately add with FS defaults (matching local mode behavior)
      setSearchText('');
      setShowDropdown(false);
      try {
        await addPredecessor(editingTaskId, {
          predecessor_task_id: task.id,
          dependency_type: 'FS',
          lag_days: 0,
        });
        fetchTasks({ preserveScroll: true });
      } catch (err) {
        console.error('Error adding predecessor:', err);
        alert('Failed to add predecessor: ' + err.message);
      }
    }
  };

  const handleAdd = async () => {
    if (!selectedPredTask) return;
    if (isLocal) {
      const newPred = {
        id: `local_${Date.now()}`,
        predecessor_task_id: selectedPredTask.id,
        dependency_type: depType,
        lag_days: lagDays,
      };
      onLocalChange([...localPredecessors, newPred]);
      setSelectedPredTask(null);
      setDepType('FS');
      setLagDays(0);
    } else {
      try {
        await addPredecessor(editingTaskId, {
          predecessor_task_id: selectedPredTask.id,
          dependency_type: depType,
          lag_days: lagDays,
        });
        setSelectedPredTask(null);
        setDepType('FS');
        setLagDays(0);
        fetchTasks({ preserveScroll: true });
      } catch (err) {
        console.error('Error adding predecessor:', err);
        alert('Failed to add predecessor: ' + err.message);
      }
    }
  };

  const handleRemove = async (predId) => {
    if (isLocal) {
      onLocalChange(localPredecessors.filter(p => p.id !== predId));
    } else {
      const ok = await confirm({ title: 'Remove Predecessor', message: 'Are you sure you want to remove this predecessor link?', confirmLabel: 'Remove', danger: true });
      if (!ok) return;
      try {
        await removePredecessor(editingTaskId, predId);
        fetchTasks({ preserveScroll: true });
      } catch (err) { console.error('Error removing predecessor:', err); }
    }
  };

  const rowStyle = {
    display: 'flex', alignItems: 'center', gap: '8px',
    padding: '8px 12px', backgroundColor: t.bgAlt, borderRadius: '8px',
    marginBottom: '6px', border: `1px solid ${t.cardBorder}`,
  };

  return (
    <div style={fieldGroup}>
      {/* Existing predecessors */}
      {currentPredecessors.map(pred => {
        const predTask = tasks.find(t => Number(t.id) === Number(pred.predecessor_task_id));
        const dt = DEP_TYPES.find(d => d.value === pred.dependency_type) || DEP_TYPES[0];
        return (
          <div key={pred.id} style={{ ...rowStyle, flexWrap: 'wrap' }}>
            <div style={{
              flex: 1, fontSize: '14.5px', fontWeight: 500, color: t.text,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              minWidth: '120px',
            }}>
              <span style={{ color: '#3b82f6', fontWeight: 600, marginRight: '6px' }}>
                {predTask?.task_code || '?'}
              </span>
              {predTask ? predTask.task_name : `Task #${pred.predecessor_task_id}`}
              {predTask && (predTask.start_date || predTask.finish_date) && (
                <div style={{ fontSize: '12.5px', color: t.textMuted, fontWeight: 400, marginTop: '2px', whiteSpace: 'nowrap' }}>
                  {predTask.start_date ? `Start: ${fmtDate(predTask.start_date)}` : ''}
                  {predTask.start_date && predTask.finish_date ? '  —  ' : ''}
                  {predTask.finish_date ? `Finish: ${fmtDate(predTask.finish_date)}` : ''}
                </div>
              )}
            </div>
            <select value={pred.dependency_type || 'FS'}
              onChange={(e) => {
                if (isLocal) {
                  onLocalChange(localPredecessors.map(p => p.id === pred.id ? { ...p, dependency_type: e.target.value } : p));
                } else {
                  updatePredecessor(editingTaskId, pred.id, { dependency_type: e.target.value, lag_days: pred.lag_days || 0 })
                    .then(() => fetchTasks({ preserveScroll: true })).catch(err => console.error('Error updating predecessor:', err));
                }
              }}
              style={{
                padding: '4px 6px', fontSize: '13.5px', border: `1px solid ${t.cardBorder}`,
                borderRadius: '6px', backgroundColor: t.bg, color: t.text,
                width: '130px', flexShrink: 0, cursor: 'pointer',
              }}>
              {DEP_TYPES.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
            </select>
            <span style={{ fontSize: '12.5px', color: t.textSecondary, flexShrink: 0 }}>Lag</span>
            <input type="number" value={pred.lag_days || 0}
              onChange={(e) => {
                const newLag = parseInt(e.target.value) || 0;
                if (isLocal) {
                  onLocalChange(localPredecessors.map(p => p.id === pred.id ? { ...p, lag_days: newLag } : p));
                } else {
                  updatePredecessor(editingTaskId, pred.id, { dependency_type: pred.dependency_type || 'FS', lag_days: newLag })
                    .then(() => fetchTasks({ preserveScroll: true })).catch(err => console.error('Error updating predecessor:', err));
                }
              }}
              style={{
                width: '40px', padding: '4px 6px', fontSize: '13.5px', textAlign: 'center',
                border: `1px solid ${t.cardBorder}`, borderRadius: '6px', backgroundColor: t.bg,
              }} />
            <span style={{ fontSize: '12.5px', color: t.textSecondary, flexShrink: 0 }}>d</span>
            <button type="button" onClick={() => handleRemove(pred.id)}
              style={{
                background: 'none', border: 'none', cursor: 'pointer', padding: '2px',
                color: t.textMuted, fontSize: '17.5px', lineHeight: 1, flexShrink: 0,
              }}
              onMouseEnter={(e) => e.currentTarget.style.color = '#ef4444'}
              onMouseLeave={(e) => e.currentTarget.style.color = '#94a3b8'}
              title="Remove predecessor"
            >&times;</button>
          </div>
        );
      })}

      {/* Selected task — show dep type + lag + confirm */}
      {selectedPredTask && (
        <div style={{ ...rowStyle, border: '1px solid #3b82f6', backgroundColor: '#eff6ff' }}>
          <div style={{
            flex: 1, fontSize: '14.5px', fontWeight: 500, color: t.text,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            <span style={{ color: '#3b82f6', fontWeight: 600, marginRight: '6px' }}>
              {selectedPredTask.task_code}
            </span>
            {selectedPredTask.task_name}
          </div>
          <select value={depType} onChange={(e) => setDepType(e.target.value)}
            style={{
              padding: '4px 6px', fontSize: '13.5px', border: '1px solid #93c5fd',
              borderRadius: '6px', backgroundColor: t.bg, color: t.text,
              width: '130px', flexShrink: 0,
            }}>
            {DEP_TYPES.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
          </select>
          <span style={{ fontSize: '12.5px', color: '#64748b', flexShrink: 0 }}>Lag</span>
          <input type="number" value={lagDays} onChange={(e) => setLagDays(parseInt(e.target.value) || 0)} min={0}
            style={{
              width: '40px', padding: '4px 6px', fontSize: '13.5px', textAlign: 'center',
              border: '1px solid #93c5fd', borderRadius: '6px', backgroundColor: '#fff',
            }} />
          <span style={{ fontSize: '12.5px', color: '#64748b', flexShrink: 0 }}>d</span>
          <button type="button" onClick={handleAdd}
            style={{
              background: '#3b82f6', border: 'none', color: '#fff', borderRadius: '6px',
              padding: '4px 10px', fontSize: '13.5px', fontWeight: 600, cursor: 'pointer', flexShrink: 0,
            }}>Add</button>
          <button type="button" onClick={() => setSelectedPredTask(null)}
            style={{
              background: 'none', border: 'none', cursor: 'pointer', padding: '2px',
              color: '#94a3b8', fontSize: '17.5px', lineHeight: 1, flexShrink: 0,
            }}>&times;</button>
        </div>
      )}

      {/* Search / Add dropdown — only show when no task is selected */}
      {!selectedPredTask && (
        <div style={{ position: 'relative' }}>
          <div style={{ position: 'relative' }}>
            <input
              type="text"
              value={searchText}
              onChange={(e) => { setSearchText(e.target.value); setShowDropdown(true); }}
              onFocus={() => setShowDropdown(true)}
              onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
              placeholder="Search tasks to add as predecessor..."
              style={{
                ...inputStyle, fontSize: '14.5px', paddingRight: '32px',
              }}
            />
            <svg style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}
              width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </div>
          {showDropdown && (
            <>
              <div style={{
                position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10,
                backgroundColor: t.bg, border: `1px solid ${t.cardBorder}`, borderRadius: '8px',
                boxShadow: '0 8px 24px rgba(0,0,0,0.12)', maxHeight: '200px', overflowY: 'auto',
                marginTop: '4px',
              }}>
                {filteredTasks.length === 0 ? (
                  <div style={{ padding: '12px 14px', fontSize: '13.5px', color: '#94a3b8', textAlign: 'center' }}>
                    No matching tasks
                  </div>
                ) : (
                  filteredTasks.map(t => (
                    <div key={t.id}
                      onClick={() => handleSelectTask(t)}
                      style={{
                        padding: '8px 14px', fontSize: '14.5px', cursor: 'pointer',
                        borderBottom: '1px solid var(--border-subtle)',
                        transition: 'background-color 0.1s',
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f0f7ff'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      <span style={{ color: '#3b82f6', fontWeight: 600, marginRight: '8px' }}>{t.task_code}</span>
                      <span style={{ color: t.text }}>{t.task_name}</span>
                      {t.building_area_phase && (
                        <span style={{ color: t.textMuted, fontSize: '12.5px', marginLeft: '8px' }}>({t.building_area_phase})</span>
                      )}
                    </div>
                  ))
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

const TasksTab = ({ projectId, refreshSummary, sharedSchedule, onTaskOpen }) => {
  const t = useTheme();
  const { confirm, confirmDialogProps } = useConfirm();
  const [_localTasks, _setLocalTasks] = useState([]);
  const tasks = sharedSchedule ? sharedSchedule.tasks : _localTasks;
  const setTasks = sharedSchedule ? sharedSchedule.setTasks : _setLocalTasks;
  const [sovLines, setSovLines] = useState([]);
  const [taskAllocations, setTaskAllocations] = useState({});
  const [showModal, setShowModal] = useState(false);
  // Default to true so the very first render shows the loading spinner
  // instead of flashing the "No tasks yet" empty state before the
  // initial fetchTasks() resolves. Flips to false in fetchTasks's finally
  // block. In sharedSchedule mode, ScheduleView owns the loading flag and
  // we read it from sharedSchedule.loading at render time.
  const [loading, setLoading] = useState(true);
  // File-import progress dialog (mirrors the one in SOVTab). Shape:
  //   null                                            -> dialog hidden
  //   { total, status: 'importing' }                  -> bulk POST in flight
  //   { total, status: 'success', count }             -> done, auto-closes
  //   { total, status: 'error', error: str }          -> failed, user dismisses
  const [importProgress, setImportProgress] = useState(null);
  const [_localCollapsedSections, _setLocalCollapsedSections] = useState({});
  const collapsedSections = sharedSchedule ? sharedSchedule.collapsedSections : _localCollapsedSections;
  const setCollapsedSections = sharedSchedule ? sharedSchedule.setCollapsedSections : _setLocalCollapsedSections;
  const [formErrors, setFormErrors] = useState({});
  const [editingPercentCompleteId, setEditingPercentCompleteId] = useState(null);
  const [editingPercentCompleteValue, setEditingPercentCompleteValue] = useState('');
  const [editingTaskId, setEditingTaskId] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  // (SOV linking is now handled inside the task Properties modal —
  // see TaskSOVLinks.js. The standalone SOVLinkModal that used to live
  // in this file was removed when the link icon on each row went away.)
  // Drag and drop state (tasks)
  const [draggedTask, setDraggedTask] = useState(null);
  const [dragOverTask, setDragOverTask] = useState(null);
  const [dragSection, setDragSection] = useState(null);
  // Drag and drop state (sections)
  const [draggedSectionName, setDraggedSectionName] = useState(null);
  const [dragOverSectionName, setDragOverSectionName] = useState(null);
  const sectionDragRef = useRef(false); // synchronous flag for section drag
  // Persisted sections from backend
  const [_localSavedSections, _setLocalSavedSections] = useState([]);
  const savedSections = sharedSchedule ? sharedSchedule.savedSections : _localSavedSections;
  const setSavedSections = sharedSchedule ? sharedSchedule.setSavedSections : _setLocalSavedSections;
  const [sectionsLoaded, setSectionsLoaded] = useState(false);

  // Filter / Sort popover open state + anchor rect. The filter/sort
  // VALUES live on ScheduleView (so the Gantt panel sees the same
  // settings), but the POPOVER for managing them lives in this
  // component since the Filter/Sort buttons are in the Tasks panel's
  // action bar.
  const [filterMenuOpen, setFilterMenuOpen] = useState(false);
  const [filterMenuAnchor, setFilterMenuAnchor] = useState(null);
  const [sortMenuOpen, setSortMenuOpen] = useState(false);
  const [sortMenuAnchor, setSortMenuAnchor] = useState(null);

  // Add Section modal state
  const [showAddSectionModal, setShowAddSectionModal] = useState(false);
  const [flaggedTaskIds, setFlaggedTaskIds] = useState(new Set());
  // Latest active Draft billing period's end date (YYYY-MM-DD string)
  // or null. Pulled from the progress-alerts-summary response. Used by
  // getMaxProgress so the client-side cap matches the server's rule:
  // tasks finishing within the active Draft AFP can be marked 100%
  // even when today is before their finish_date.
  const [latestDraftPeriodEnd, setLatestDraftPeriodEnd] = useState(null);
  const [_localDepAlertData, _setLocalDepAlertData] = useState({ alerts: [], flagged_task_ids: [], summary: {} });
  const depAlertData = sharedSchedule ? sharedSchedule.depAlerts : _localDepAlertData;
  const setDepAlertData = sharedSchedule ? () => {} : _setLocalDepAlertData;
  // Pending predecessors for the Add Task modal (saved after task is created)
  const [pendingPredecessors, setPendingPredecessors] = useState([]);
  // Pending SOV links for the Add Task modal — collected locally, then
  // committed as allocations once the task has an id (same pattern as
  // pendingPredecessors, since an allocation needs a saved task_id).
  const [pendingSovLinks, setPendingSovLinks] = useState([]);
  const [newSectionName, setNewSectionName] = useState('');
  const [sectionNameError, setSectionNameError] = useState('');
  // Double-submit guards. `creatingTask` / `creatingSection` block the
  // primary modal submit buttons; `savingEdit` blocks the inline
  // properties-panel Save Changes button; `deletingTaskId` per-task so
  // one slow delete doesn't freeze unrelated row controls.
  const [creatingTask, setCreatingTask] = useState(false);
  const [creatingSection, setCreatingSection] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  const [deletingTaskId, setDeletingTaskId] = useState(null);
  const [formData, setFormData] = useState({
    task_code: '',
    task_name: '',
    task_description: '',
    building_area_phase: '',
    task_type: 'Labor',
    start_date: '',
    finish_date: '',
    duration_days: 0,
    status: 'Not Started',
    percent_complete: 0,
    responsible_party: '',
    client_visible: true,
    milestone_flag: false,
    notes: ''
  });

  // Helper: get all existing section names (from tasks + persisted sections)
  const getExistingSections = () => {
    const sections = new Set();
    tasks.forEach(t => {
      const sec = (t.building_area_phase || '').trim();
      if (sec) sections.add(sec);
    });
    savedSections.forEach(s => sections.add(s.section_name));
    return Array.from(sections);
  };

  // Check if a section name already exists (case-insensitive), optionally excluding a specific original name
  const isSectionNameTaken = (name, excludeOriginal = null) => {
    const normalized = (name || '').trim().toLowerCase();
    if (!normalized) return false;
    return getExistingSections().some(sec => {
      if (excludeOriginal && sec.toLowerCase() === excludeOriginal.toLowerCase()) return false;
      return sec.toLowerCase() === normalized;
    });
  };

  // Fetch sections from backend
  const fetchSections = async () => {
    try {
      const data = await getScheduleSections(projectId);
      console.log('[fetchSections] Loaded:', data.map(s => `${s.section_name}(${s.sort_order})`));
      setSavedSections(data);
      setSectionsLoaded(true);
    } catch (error) {
      console.error('Error fetching sections:', error);
      setSectionsLoaded(true);
    }
  };

  // Refresh both alert sources from the server. Pulled out of the
  // initial useEffect so handlePercentCompleteChange (and any future
  // task-update handler) can re-run it after a save — without this,
  // amber borders on rows that were "behind schedule" linger after
  // the PM marks them caught-up because the client-cached
  // flaggedTaskIds / depAlertData stay frozen at mount-time.
  //
  // Debounced (800 ms) so rapid edits — slider drags, bulk imports,
  // PMs marking N tasks done back-to-back — collapse into a single
  // server round-trip instead of N. Without this, every save was
  // firing 2 extra GETs and rapidly chewing through the 500/min
  // rate limit. Pass { immediate: true } to fire right now (used
  // on mount).
  const alertRefreshTimerRef = useRef(null);
  const refreshTaskAlerts = useCallback(({ immediate = false } = {}) => {
    const doFetch = () => {
      alertRefreshTimerRef.current = null;
      getProgressAlertsSummary(projectId).then(data => {
        const ids = new Set();
        (data.summaries || []).forEach(s => (s.flagged_task_ids || []).forEach(id => ids.add(id)));
        (data.overdue_task_ids || []).forEach(id => ids.add(id));
        setFlaggedTaskIds(ids);
        setLatestDraftPeriodEnd(data.latest_draft_period_end || null);
      }).catch(console.error);
      if (!sharedSchedule) {
        getDependencyAlerts(projectId).then(data => {
          setDepAlertData(data);
        }).catch(console.error);
      }
    };
    if (alertRefreshTimerRef.current) clearTimeout(alertRefreshTimerRef.current);
    if (immediate) doFetch();
    else alertRefreshTimerRef.current = setTimeout(doFetch, 800);
  }, [projectId, sharedSchedule]);

  // Cancel any pending refresh on unmount / project change so we don't
  // setState on an unmounted component.
  useEffect(() => () => {
    if (alertRefreshTimerRef.current) {
      clearTimeout(alertRefreshTimerRef.current);
      alertRefreshTimerRef.current = null;
    }
  }, [projectId]);

  // Optimistic local flag-clear: if the user just marked a task ≥100%
  // (Complete), strip it from flaggedTaskIds immediately so the amber
  // border disappears with no waiting on the server. Server will
  // confirm via refreshTaskAlerts after the debounce. We don't try to
  // optimistically re-flag in the other direction — server is the
  // authority on partial-progress overdue logic.
  const clearFlagLocally = useCallback((taskId) => {
    setFlaggedTaskIds(prev => {
      if (!prev.has(taskId)) return prev;
      const next = new Set(prev);
      next.delete(taskId);
      return next;
    });
  }, []);

  // Fetch tasks-only data (SOV lines, progress alerts, dep alerts) when the
  // project changes. When sharedSchedule owns the task list, we skip the
  // tasks/sections/depAlerts fetches; ScheduleView handles those.
  useEffect(() => {
    if (!sharedSchedule) {
      fetchTasks();
      fetchSections();
    }
    fetchSOVLines();
    refreshTaskAlerts({ immediate: true });
  }, [projectId, refreshTaskAlerts]);

  // Allocations are fetched on-demand (when the SOV link panel opens for a
  // specific task), not in bulk on mount — bulk-loading N allocations in
  // parallel was eating the request budget for large projects.

  // Renumber all tasks in all sections so codes are sequential starting at 001
  const renumberAllSections = async (taskList) => {
    const grouped = {};
    taskList.forEach(t => {
      const sec = t.building_area_phase || 'Unsectioned';
      if (!grouped[sec]) grouped[sec] = [];
      grouped[sec].push(t);
    });

    let needsRefresh = false;
    for (const section of Object.keys(grouped)) {
      // Sort by existing code so we preserve current order
      grouped[section].sort((a, b) => (a.task_code || '').localeCompare(b.task_code || '', undefined, { numeric: true }));
      for (let i = 0; i < grouped[section].length; i++) {
        const correctCode = generateTaskCode(section, i + 1);
        if (grouped[section][i].task_code !== correctCode) {
          try {
            await updateTask(grouped[section][i].id, { task_code: correctCode });
            grouped[section][i].task_code = correctCode;
            needsRefresh = true;
          } catch (e) { /* ignore */ }
        }
      }
    }
    return needsRefresh;
  };

  const fetchTasks = async ({ preserveScroll = false } = {}) => {
    if (sharedSchedule) {
      await sharedSchedule.refreshTasks();
      // Refresh allocations alongside since the SOV Links column needs them.
      // Fetch the current list directly — sharedSchedule.tasks is a closure
      // snapshot from render time and won't include a task just created this
      // tick, so iterating it would skip the new task's allocations (leaving
      // its SOV-link badge blank even though the links were saved).
      try {
        const fresh = await getTasks(projectId);
        for (const task of fresh) {
          try {
            const allocs = await getTaskAllocations(task.id);
            setTaskAllocations(prev => ({ ...prev, [task.id]: allocs }));
          } catch (e) { /* ignore */ }
        }
      } catch (e) { /* ignore */ }
      return;
    }
    // Save scroll positions before re-fetching so we can restore them
    const savedWindowScroll = preserveScroll ? window.scrollY : 0;
    const scrollableEls = preserveScroll ? Array.from(document.querySelectorAll('[style*="overflow"]')).filter(el => el.scrollTop > 0).map(el => ({ el, top: el.scrollTop })) : [];

    try {
      // Only show loading spinner on initial load, not on preserveScroll refreshes
      if (!preserveScroll) setLoading(true);

      let data = await getTasks(projectId);

      // Auto-renumber if any codes are out of sequence
      const didRenumber = await renumberAllSections(data);
      if (didRenumber) {
        data = await getTasks(projectId);
      }

      setTasks(data);
      // Fetch allocations for all tasks to populate SOV Links column
      const allocMap = {};
      for (const task of data) {
        try {
          const allocs = await getTaskAllocations(task.id);
          allocMap[task.id] = allocs;
        } catch (e) { /* ignore */ }
      }
      setTaskAllocations(prev => ({ ...prev, ...allocMap }));

      // Restore scroll positions after React re-renders with new data
      if (preserveScroll) {
        // Double rAF ensures we restore AFTER React has committed the DOM update
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            window.scrollTo(0, savedWindowScroll);
            scrollableEls.forEach(({ el, top }) => { if (document.contains(el)) el.scrollTop = top; });
          });
        });
      }
    } catch (error) {
      console.error('Error fetching tasks:', error);
    } finally {
      if (!preserveScroll) setLoading(false);
    }
  };

  const fetchSOVLines = async () => {
    try {
      const data = await getSOVLines(projectId);
      setSovLines(data);
    } catch (error) {
      console.error('Error fetching SOV lines:', error);
    }
  };

  const fetchTaskAllocations = async (taskId) => {
    try {
      const data = await getTaskAllocations(taskId);
      setTaskAllocations(prev => ({ ...prev, [taskId]: data }));
    } catch (error) {
      console.error('Error fetching task allocations:', error);
    }
  };

  // Auto-calculate duration_days when dates change
  useEffect(() => {
    if (formData.start_date && formData.finish_date) {
      const start = new Date(formData.start_date);
      const finish = new Date(formData.finish_date);
      const duration = Math.ceil((finish - start) / (1000 * 60 * 60 * 24)) + 1;
      setFormData(prev => ({ ...prev, duration_days: Math.max(0, duration) }));
    }
  }, [formData.start_date, formData.finish_date]);

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    let newData = { [name]: type === 'checkbox' ? checked : value };
    // When start date changes and finish date is before the new start, clear finish date
    if (name === 'start_date' && value && formData.finish_date && formData.finish_date < value) {
      newData.finish_date = '';
    }
    setFormData(prev => ({ ...prev, ...newData }));
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleAddTask = async () => {
    if (creatingTask) return;
    const errors = {};
    if (!formData.task_name || !formData.task_name.trim()) errors.task_name = 'Task name is required';
    if (!formData.start_date) errors.start_date = 'Start date is required';
    if (!formData.finish_date) errors.finish_date = 'Finish date is required';
    if (formData.start_date && formData.finish_date) {
      if (new Date(formData.finish_date) < new Date(formData.start_date)) {
        errors.finish_date = 'Finish date cannot be before start date';
      }
    }
    if (!formData.building_area_phase) errors.building_area_phase = 'Please select a section';

    // Validate dates against predecessor dependencies
    if (pendingPredecessors.length > 0 && formData.start_date) {
      for (const pred of pendingPredecessors) {
        const predTask = tasks.find(t => Number(t.id) === Number(pred.predecessor_task_id));
        if (!predTask) continue;
        const lag = pred.lag_days || 0;
        const addDays = (dateStr, days) => {
          const d = new Date(dateStr + 'T00:00:00');
          d.setDate(d.getDate() + days);
          return d;
        };

        switch (pred.dependency_type) {
          case 'FS': // Finish to Start: new start >= pred finish + lag
            if (predTask.finish_date && formData.start_date) {
              const earliest = addDays(predTask.finish_date, lag);
              if (new Date(formData.start_date + 'T00:00:00') < earliest) {
                errors.start_date = `Start date must be on or after ${earliest.toLocaleDateString('en-US')} (${predTask.task_code} finishes ${new Date(predTask.finish_date + 'T00:00:00').toLocaleDateString('en-US')}${lag ? ` + ${lag}d lag` : ''})`;
              }
            }
            break;
          case 'SS': // Start to Start: new start >= pred start + lag
            if (predTask.start_date && formData.start_date) {
              const earliest = addDays(predTask.start_date, lag);
              if (new Date(formData.start_date + 'T00:00:00') < earliest) {
                errors.start_date = `Start date must be on or after ${earliest.toLocaleDateString('en-US')} (${predTask.task_code} starts ${new Date(predTask.start_date + 'T00:00:00').toLocaleDateString('en-US')}${lag ? ` + ${lag}d lag` : ''})`;
              }
            }
            break;
          case 'FF': // Finish to Finish: new finish >= pred finish + lag
            if (predTask.finish_date && formData.finish_date) {
              const earliest = addDays(predTask.finish_date, lag);
              if (new Date(formData.finish_date + 'T00:00:00') < earliest) {
                errors.finish_date = `Finish date must be on or after ${earliest.toLocaleDateString('en-US')} (${predTask.task_code} finishes ${new Date(predTask.finish_date + 'T00:00:00').toLocaleDateString('en-US')}${lag ? ` + ${lag}d lag` : ''})`;
              }
            }
            break;
          case 'SF': // Start to Finish: new finish >= pred start + lag
            if (predTask.start_date && formData.finish_date) {
              const earliest = addDays(predTask.start_date, lag);
              if (new Date(formData.finish_date + 'T00:00:00') < earliest) {
                errors.finish_date = `Finish date must be on or after ${earliest.toLocaleDateString('en-US')} (${predTask.task_code} starts ${new Date(predTask.start_date + 'T00:00:00').toLocaleDateString('en-US')}${lag ? ` + ${lag}d lag` : ''})`;
              }
            }
            break;
          default:
            break;
        }
        // Stop at the first violation so the message is clear
        if (errors.start_date || errors.finish_date) break;
      }
    }

    if (Object.keys(errors).length > 0) { setFormErrors(errors); return; }

    try {
      setCreatingTask(true);
      // Auto-assign task code based on position in section
      const section = formData.building_area_phase || 'Unsectioned';
      const sectionTasks = tasks.filter(t => (t.building_area_phase || 'Unsectioned') === section);
      const newCode = generateTaskCode(section, sectionTasks.length + 1);

      // Calculate duration
      let duration = 0;
      if (formData.start_date && formData.finish_date) {
        const s = new Date(formData.start_date);
        const f = new Date(formData.finish_date);
        duration = Math.max(1, Math.ceil((f - s) / (1000 * 60 * 60 * 24)) + 1);
      }

      // Cap progress the same way the edit path does: a task can't be more
      // complete than its schedule has elapsed. POST /tasks doesn't enforce this
      // (PUT does), and the Status dropdown can request 100/50 — so clamp here to
      // getMaxProgress and keep status consistent, or a future task marked
      // "Complete" would persist an inflated earned_amount on any linked SOV line.
      const rawPct = parseFloat(formData.percent_complete) || 0;
      const maxPct = getMaxProgress({ start_date: formData.start_date, finish_date: formData.finish_date });
      const cappedPct = Math.max(0, Math.min(maxPct, rawPct));
      const cappedStatus = cappedPct >= 100 ? 'Complete' : cappedPct > 0 ? 'In Progress' : 'Not Started';

      const taskData = {
        project_id: projectId,
        task_code: newCode,
        task_name: formData.task_name,
        task_description: formData.task_description || '',
        building_area_phase: formData.building_area_phase,
        task_type: formData.task_type || 'Labor',
        start_date: formData.start_date,
        finish_date: formData.finish_date,
        duration_days: duration,
        status: cappedStatus,
        percent_complete: cappedPct,
        responsible_party: formData.responsible_party || '',
        client_visible: formData.client_visible ? 1 : 0,
        milestone_flag: formData.milestone_flag ? 1 : 0,
        notes: formData.notes || '',
      };

      const newTask = await createTask(taskData);

      // Save any pending predecessors
      let failedPreds = 0;
      if (pendingPredecessors.length > 0 && newTask?.id) {
        for (const pred of pendingPredecessors) {
          try {
            await addPredecessor(newTask.id, {
              predecessor_task_id: pred.predecessor_task_id,
              dependency_type: pred.dependency_type,
              lag_days: pred.lag_days,
            });
          } catch (predErr) {
            console.error('Error adding predecessor:', predErr);
            failedPreds += 1;
          }
        }
      }

      // Commit any pending SOV links — an allocation needs the saved task_id,
      // so (like predecessors) they were collected locally and are created now.
      let failedLinks = 0;
      if (pendingSovLinks.length > 0 && newTask?.id) {
        for (const link of pendingSovLinks) {
          try {
            await createAllocation({
              project_id: projectId,
              task_id: newTask.id,
              sov_line_id: link.sov_line_id,
              allocation_method: link.allocation_method,
              allocation_percent: link.allocation_percent,
              allocation_amount: link.allocation_amount,
            });
          } catch (allocErr) {
            console.error('Error adding SOV link:', allocErr);
            failedLinks += 1;
          }
        }
      }

      // The task itself was created; if any linked extras failed to attach, say
      // so rather than reporting a clean success — otherwise the user thinks
      // their SOV links / predecessors saved when they didn't.
      if (failedPreds || failedLinks) {
        const parts = [];
        if (failedLinks) parts.push(`${failedLinks} SOV link${failedLinks === 1 ? '' : 's'}`);
        if (failedPreds) parts.push(`${failedPreds} predecessor${failedPreds === 1 ? '' : 's'}`);
        alert(`Task "${taskData.task_name}" was created, but ${parts.join(' and ')} couldn't be saved — add ${parts.length > 1 || (failedLinks > 1 || failedPreds > 1) ? 'them' : 'it'} from the task's Properties.`);
      }

      setShowModal(false);
      setFormErrors({});
      setPendingPredecessors([]);
      setPendingSovLinks([]);
      setFormData({
        task_code: '', task_name: '', task_description: '', building_area_phase: '',
        task_type: 'Labor', start_date: '', finish_date: '', duration_days: 0,
        status: 'Not Started', percent_complete: 0,
        responsible_party: '', client_visible: true, milestone_flag: false, notes: ''
      });
      fetchTasks();
      refreshSummary();
    } catch (error) {
      console.error('Error creating task:', error);
      alert('Failed to create task: ' + error.message);
    } finally {
      setCreatingTask(false);
    }
  };

  const handleUpdateTask = async (taskId, updates, { preserveScroll = false } = {}) => {
    try {
      await updateTask(taskId, updates);
      fetchTasks({ preserveScroll });
      refreshSummary();
      refreshTaskAlerts();
    } catch (error) {
      console.error('Error updating task:', error);
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (deletingTaskId) return;
    const ok = await confirm({ title: 'Delete Task', message: 'Are you sure you want to delete this task? This action cannot be undone.', confirmLabel: 'Delete', danger: true });
    if (ok) {
      try {
        setDeletingTaskId(taskId);
        // Find the task's section before deleting
        const taskToDelete = tasks.find(t => t.id === taskId);
        const section = taskToDelete ? (taskToDelete.building_area_phase || 'Unsectioned') : null;

        await deleteTask(taskId);
        setEditingTaskId(null);

        // Fetch fresh task list, then renumber the section
        const freshTasks = await getTasks(projectId);
        setTasks(freshTasks);

        if (section) {
          const sectionTasks = freshTasks
            .filter(t => (t.building_area_phase || 'Unsectioned') === section)
            .sort((a, b) => (a.task_code || '').localeCompare(b.task_code || '', undefined, { numeric: true }));

          for (let i = 0; i < sectionTasks.length; i++) {
            const newCode = generateTaskCode(section, i + 1);
            if (sectionTasks[i].task_code !== newCode) {
              await updateTask(sectionTasks[i].id, { task_code: newCode });
            }
          }
          // Refresh again to show updated codes
          fetchTasks({ preserveScroll: true });
        }
      } catch (error) {
        console.error('Error deleting task:', error);
      } finally {
        setDeletingTaskId(null);
      }
    }
  };

  // Calculate max allowed progress for a task based on schedule proration.
  // Reference date = the active Draft AFP's period_end_date if one
  // exists on the project, otherwise today. Matches the server-side
  // proration rule in tasks.js PUT /:id so the slider, the validation
  // alert, AND the eventual save all agree on the cap. Lets PMs mark a
  // task finishing May 28 as 100% on May 25 when the billing period
  // ends May 31.
  const getMaxProgress = (task) => {
    if (!task || !task.start_date || !task.finish_date) return 100;
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    const draftEnd = latestDraftPeriodEnd
      ? new Date(latestDraftPeriodEnd + 'T23:59:59')
      : null;
    const refDate = draftEnd || today;
    const taskStart = new Date(task.start_date + 'T00:00:00');
    const taskEnd = new Date(task.finish_date + 'T23:59:59');
    const dayMs = 1000 * 60 * 60 * 24;
    const totalDuration = Math.max(1, Math.round((taskEnd - taskStart) / dayMs) + 1);
    if (refDate < taskStart) return 0;
    if (refDate >= taskEnd) return 100;
    const daysElapsed = Math.max(1, Math.round((refDate - taskStart) / dayMs) + 1);
    return Math.min(100, Math.ceil((daysElapsed / totalDuration) * 100));
  };

  const handlePercentCompleteChange = async (taskId, newValue) => {
    const task = tasks.find(tk => tk.id === taskId);
    // Snapshot prior values for rollback if the save fails — otherwise
    // a 429 / 400 / network blip wipes the optimistic % to whatever's
    // in the DB (usually 0) with no explanation to the user.
    const prevPct = parseFloat(task?.percent_complete) || 0;
    const prevStatus = task?.status || 'Not Started';
    const maxPct = getMaxProgress(task);
    // Hard clamp: never allow more than the prorated max
    const percentComplete = Math.min(maxPct, Math.max(0, parseInt(newValue) || 0));

    // Show warning if user tried to exceed the cap
    if (parseInt(newValue) > maxPct && maxPct < 100) {
      if (maxPct === 0) {
        confirm({
          title: 'Cannot Set Progress',
          message: `This task is scheduled entirely in the future (starts ${task.start_date}). Progress must remain at 0% because work cannot be billed for dates that haven't occurred yet.`,
          confirmLabel: 'OK',
          danger: false,
        });
      } else {
        // Phrase the cap relative to whichever reference is in effect.
        // matches getMaxProgress + the server-side check.
        const refLabel = latestDraftPeriodEnd
          ? `the active billing period ends ${latestDraftPeriodEnd}`
          : `today is ${new Date().toISOString().split('T')[0]}`;
        confirm({
          title: 'Progress Capped',
          message: `This task is scheduled ${task.start_date} – ${task.finish_date}. By ${refLabel}, only ${maxPct}% of the schedule will have elapsed. Maximum allowed progress: ${maxPct}%. To enter more, adjust the task finish date or extend the billing period.`,
          confirmLabel: 'OK',
          danger: false,
        });
      }
      // Still save the clamped value (don't return early) so the UI stays consistent
      if (percentComplete === (parseFloat(task?.percent_complete) || 0)) return;
    }

    // Determine new status based on percentage
    let newStatus = 'Not Started';
    if (percentComplete >= 100) newStatus = 'Complete';
    else if (percentComplete > 0) newStatus = 'In Progress';
    // Optimistic update: patch local state immediately so no re-fetch/re-render flicker
    setTasks(prev => prev.map(t =>
      t.id === taskId ? { ...t, percent_complete: percentComplete, status: newStatus } : t
    ));
    // If the task just hit 100%, clear its amber border locally too — server
    // will reconfirm on the debounced refresh, but the UI shouldn't wait.
    if (percentComplete >= 100) clearFlagLocally(taskId);
    // Save to server in the background — no fetchTasks, no await.
    // After a successful save, debounced-refresh the progress/overdue/dep
    // alerts so flaggedTaskIds eventually reconverges with the server.
    updateTask(taskId, { percent_complete: percentComplete, status: newStatus })
      .then(() => {
        refreshSummary();
        refreshTaskAlerts();
      })
      .catch((error) => {
        console.error('Error updating task:', error);
        // Local rollback only — no extra fetchTasks request (which would
        // itself be rate-limited). Restore the row to its prior state
        // and tell the user the save didn't land.
        setTasks(prev => prev.map(t =>
          t.id === taskId ? { ...t, percent_complete: prevPct, status: prevStatus } : t
        ));
        const msg = error?.message || String(error);
        alert(`Failed to save progress: ${msg}`);
      });
  };

  // ============ DRAG AND DROP ============
  const handleDragStart = (e, task, section) => {
    if (sectionDragRef.current) { e.preventDefault(); return; } // Don't start task drag during section drag
    setDraggedTask(task);
    setDragSection(section);
    e.dataTransfer.setData('text/plain', 'task:' + task.id);
    e.dataTransfer.effectAllowed = 'move';
    e.currentTarget.style.opacity = '0.5';
  };

  const handleDragEnd = (e) => {
    e.currentTarget.style.opacity = '1';
    setDraggedTask(null);
    setDragOverTask(null);
    setDragSection(null);
  };

  const handleDragOver = (e, task) => {
    if (sectionDragRef.current) return; // Section drag in progress — let it bubble
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (task.id !== dragOverTask) {
      setDragOverTask(task.id);
    }
  };

  const handleDrop = async (e, targetTask, section) => {
    if (sectionDragRef.current) return; // Section drag in progress — let it bubble
    e.preventDefault();
    if (!draggedTask || draggedTask.id === targetTask.id) {
      setDraggedTask(null);
      setDragOverTask(null);
      return;
    }

    // Only allow reorder within same section
    if (dragSection !== section) {
      setDraggedTask(null);
      setDragOverTask(null);
      return;
    }

    // Whole-list reorder, but only when both drag + drop are in the
    // same section (the guard above already returned early otherwise).
    // The grouping UI buckets by building_area_phase regardless of
    // sort_order, so dragging cross-section would leave the task
    // visually in its original section anyway -- confusing. Within
    // section we still rewrite GLOBAL sort_order so the server's
    // positional task_code stays sequential across the whole list.
    const ordered = [...tasks].sort(
      (a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)
    );
    const dragIdx = ordered.findIndex(t => t.id === draggedTask.id);
    const dropIdx = ordered.findIndex(t => t.id === targetTask.id);
    if (dragIdx === -1 || dropIdx === -1) return;

    const [moved] = ordered.splice(dragIdx, 1);
    ordered.splice(dropIdx, 0, moved);

    // Reassign sort_order to match the new positions. Use index*10 so
    // there's room to slot a new task in between later without renum-
    // bering everything (cheap optimization, harmless if unused).
    const updates = ordered.map((task, idx) => ({
      id: task.id,
      newSortOrder: (idx + 1) * 10,
    }));

    // Optimistic UI: rewrite sort_order locally AND patch task_code so
    // the row labels update immediately (server will return the same
    // positional codes on next fetch). We stamp `TSK-NNNN` here using
    // the same rule the server uses (idx + 1, zero-padded to 4).
    setTasks(ordered.map((t, idx) => ({
      ...t,
      sort_order: updates[idx].newSortOrder,
      task_code: `TSK-${String(idx + 1).padStart(4, '0')}`,
    })));

    setDraggedTask(null);
    setDragOverTask(null);

    // Persist sort_order to the backend. task_code is recomputed at
    // read time on the server (see server/routes/tasks.js), so we don't
    // bother writing it -- the next GET regenerates it.
    for (const upd of updates) {
      try {
        await updateTask(upd.id, { sort_order: upd.newSortOrder });
      } catch (err) {
        console.error('Error updating task sort_order:', err);
      }
    }
  };

  // ============ SECTION DRAG AND DROP ============
  const handleSectionDragStart = (e, sectionName) => {
    e.stopPropagation();
    sectionDragRef.current = true;
    setDraggedSectionName(sectionName);
    e.dataTransfer.setData('text/plain', 'section:' + sectionName);
    e.dataTransfer.effectAllowed = 'move';
    e.currentTarget.style.opacity = '0.5';
  };

  const handleSectionDragEnd = (e) => {
    e.currentTarget.style.opacity = '1';
    sectionDragRef.current = false;
    setDraggedSectionName(null);
    setDragOverSectionName(null);
  };

  const handleSectionDragOver = (e, sectionName) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (sectionName !== dragOverSectionName) {
      setDragOverSectionName(sectionName);
    }
  };

  const sectionsRef = useRef([]);

  const handleSectionDrop = async (e, targetSection) => {
    e.preventDefault();
    e.stopPropagation();

    const dragged = draggedSectionName;
    sectionDragRef.current = false;
    setDraggedSectionName(null);
    setDragOverSectionName(null);

    if (!dragged || dragged === targetSection) return;

    const currentOrder = [...sectionsRef.current];
    const dragIdx = currentOrder.indexOf(dragged);
    const dropIdx = currentOrder.indexOf(targetSection);
    if (dragIdx < 0 || dropIdx < 0) return;

    // Reorder
    currentOrder.splice(dragIdx, 1);
    currentOrder.splice(dropIdx, 0, dragged);

    // Optimistic UI — update savedSections immediately so render reflects new order
    const optimistic = currentOrder.map((name, i) => {
      const existing = savedSections.find(s => s.section_name === name);
      return existing ? { ...existing, sort_order: i } : { section_name: name, sort_order: i, project_id: projectId };
    });
    setSavedSections(optimistic);

    // Persist to backend
    try {
      console.log('[SectionDrop] Saving order:', currentOrder);
      const updated = await reorderScheduleSections(projectId, currentOrder);
      console.log('[SectionDrop] Server returned:', updated.map(s => `${s.section_name}(${s.sort_order})`));
      setSavedSections(updated);
    } catch (err) {
      console.error('[SectionDrop] Error reordering sections:', err);
    }
  };

  // (SOV linking handlers used to live here. They were only called by
  // the standalone SOVLinkModal — both the modal and its triggers were
  // removed when SOV linking moved into TaskSOVLinks inside the task
  // Properties dialog.)

  // ============ GROUP & SORT TASKS ============
  // When sharedSchedule is provided, use the parent's precomputed groups/sections
  // so both Tasks and Gantt render the same rows in the same order.
  const _localGroupedTasks = tasks.reduce((acc, task) => {
    const section = task.building_area_phase || 'Unsectioned';
    if (!acc[section]) acc[section] = [];
    acc[section].push(task);
    return acc;
  }, {});
  Object.keys(_localGroupedTasks).forEach(section => {
    _localGroupedTasks[section].sort((a, b) => (a.task_code || '').localeCompare(b.task_code || '', undefined, { numeric: true }));
  });
  const groupedTasks = sharedSchedule ? sharedSchedule.groupedTasks : _localGroupedTasks;

  const savedSectionNames = savedSections.map(s => s.section_name);
  const allSectionNames = new Set([...savedSectionNames, ...Object.keys(groupedTasks)]);
  const savedSet = new Set(savedSectionNames);
  const unsavedNames = Array.from(allSectionNames).filter(n => !savedSet.has(n)).sort();
  const _localSections = [...savedSectionNames.filter(n => allSectionNames.has(n)), ...unsavedNames];
  const sections = sharedSchedule ? sharedSchedule.sections : _localSections;
  sectionsRef.current = sections;

  // Display-layer views — these honor the schedule's search /
  // filter / sort settings so the Tasks panel stays in lockstep with
  // the Gantt panel. The drag-and-drop machinery above STILL operates
  // on the canonical `sections` + `groupedTasks` lists so reordering
  // works regardless of what's filtered out.
  const displayGroupedTasks =
    (sharedSchedule && sharedSchedule.filteredGroupedTasks) || groupedTasks;
  const displaySections =
    (sharedSchedule && sharedSchedule.filteredSections) || sections;

  // Auto-persist section order when there are unsaved sections (from tasks not yet in sections table)
  useEffect(() => {
    if (sectionsLoaded && unsavedNames.length > 0 && sections.length > 0 && projectId) {
      reorderScheduleSections(projectId, sections).then(updated => {
        setSavedSections(updated);
      }).catch(err => console.error('Error auto-saving section order:', err));
    }
  }, [sectionsLoaded, unsavedNames.length, projectId]); // eslint-disable-line

  const toggleSection = (section) => {
    setCollapsedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // ============ PERCENT COMPLETE INLINE EDIT ============
  const handlePercentCompleteDoubleClick = (taskId, currentValue) => {
    setEditingPercentCompleteId(taskId);
    setEditingPercentCompleteValue(currentValue || 0);
  };

  const handlePercentCompleteEditSave = (taskId) => {
    const val = editingPercentCompleteValue;
    setEditingPercentCompleteId(null);
    setEditingPercentCompleteValue('');
    // Fire the update without awaiting — optimistic local state handles the UI instantly
    handlePercentCompleteChange(taskId, val);
  };

  const handlePercentCompleteEditCancel = () => {
    setEditingPercentCompleteId(null);
    setEditingPercentCompleteValue('');
  };

  // ============ XLSX TEMPLATE & IMPORT/EXPORT ============
  const handleDownloadTaskTemplate = () => {
    const wb = XLSX.utils.book_new();
    // predecessors + sov_links are the two new "one cell, many values"
    // columns added to the template. Format documented in the Field
    // Guide sheet and parsed client-side in handleImportTaskFile.
    const headers = [
      'task_code', 'task_name', 'task_description', 'building_area_phase',
      'task_type', 'start_date', 'finish_date', 'responsible_party',
      'client_visible', 'milestone_flag', 'predecessors', 'sov_links', 'notes',
    ];
    const sampleRows = [
      ['HS-001', 'Material Delivery',  'Deliver materials to site', 'High School', 'Procurement', '2026-04-01', '2026-04-05', 'John Smith', 'true',  'false', '',                        'HS-LABOR:25',                  'Example task'],
      ['HS-002', 'Demo Old Unit',      'Remove existing equipment', 'High School', 'Labor',       '2026-04-06', '2026-04-10', 'Jane Doe',   'true',  'false', 'HS-001',                  'HS-LABOR:25',                  ''],
      ['HS-003', 'Install New Unit',   'Install and connect',       'High School', 'Labor',       '2026-04-11', '2026-04-20', 'John Smith', 'true',  'false', 'HS-002:FS:2',             'HS-LABOR:50;HS-MAT:$15000',    'Example with lag + amount link'],
    ];
    const ws = XLSX.utils.aoa_to_sheet([headers, ...sampleRows]);
    ws['!cols'] = [
      { wch: 12 }, { wch: 25 }, { wch: 30 }, { wch: 20 }, { wch: 15 },
      { wch: 14 }, { wch: 14 }, { wch: 18 }, { wch: 14 }, { wch: 14 },
      { wch: 22 }, { wch: 30 }, { wch: 30 },
    ];
    XLSX.utils.book_append_sheet(wb, ws, 'Task Data');
    const legendData = [
      ['FIELD', 'VALID OPTIONS / FORMAT'], [''],
      ['task_type', 'Labor, Material, Procurement, Engineering, Inspection, Milestone'],
      ['start_date / finish_date', 'YYYY-MM-DD format (e.g., 2026-04-01)'],
      ['client_visible', 'true or false'],
      ['milestone_flag', 'true or false'],
      ['building_area_phase', 'Groups tasks into sections (e.g., High School, Junior High)'],
      [''],
      ['predecessors', 'Semicolon-separated list of task codes that must run before this task.'],
      ['', 'Each entry: TASK_CODE[:DEP_TYPE[:LAG_DAYS]].  DEP_TYPE default FS, lag default 0.'],
      ['', 'DEP_TYPE values: FS (finish-to-start), SS, FF, SF.'],
      ['', 'Examples:  HS-001         → FS, 0 lag'],
      ['', '            HS-001;HS-002 → two predecessors, both FS+0'],
      ['', '            HS-002:SS:2   → start-to-start, 2 days lag'],
      ['', 'You can reference task codes defined in OTHER rows of this same import.'],
      [''],
      ['sov_links', 'Semicolon-separated list of SOV-line allocations attached to this task.'],
      ['', 'Each entry: SOV_CODE:VALUE. VALUE is a percentage by default (e.g., 50 = 50%).'],
      ['', 'Prefix the value with $ for a dollar amount instead (e.g., $25000).'],
      ['', 'Examples:  HS-LABOR:100              → 100% of HS-LABOR allocated here'],
      ['', '            HS-LABOR:50;HS-MAT:50     → split 50/50 across two SOV lines'],
      ['', '            HS-LABOR:25;HS-MAT:$15000 → 25% of HS-LABOR + $15,000 of HS-MAT'],
      ['', 'SOV codes must already exist on this project — create SOV lines first.'],
    ];
    const wsLegend = XLSX.utils.aoa_to_sheet(legendData);
    wsLegend['!cols'] = [{ wch: 24 }, { wch: 80 }];
    XLSX.utils.book_append_sheet(wb, wsLegend, 'Field Guide');
    XLSX.writeFile(wb, 'tasks_template.xlsx');
  };

  const parseTaskFileRows = async (file) => {
    const ext = file.name.split('.').pop().toLowerCase();
    if (ext === 'xlsx' || ext === 'xls' || ext === 'xlsm') {
      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: 'array', cellDates: true });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      return XLSX.utils.sheet_to_json(sheet, { defval: '', raw: false });
    } else {
      const text = await file.text();
      const lines = text.split('\n').map(line => {
        const result = []; let current = ''; let inQuotes = false;
        for (let i = 0; i < line.length; i++) {
          const char = line[i];
          if (char === '"') inQuotes = !inQuotes;
          else if (char === ',' && !inQuotes) { result.push(current.trim()); current = ''; }
          else current += char;
        }
        result.push(current.trim());
        return result;
      });
      if (lines.length < 2) return [];
      const headers = lines[0].map(h => h.toLowerCase().trim());
      const rows = [];
      for (let i = 1; i < lines.length; i++) {
        const row = lines[i];
        if (!row || row.length < 2 || !row[0] || row[0].startsWith('---')) continue;
        const rowData = {};
        headers.forEach((header, idx) => { if (idx < row.length) rowData[header] = row[idx]; });
        rows.push(rowData);
      }
      return rows;
    }
  };

  const normalizeDate = (val) => {
    if (!val) return '';
    if (val instanceof Date) {
      return `${val.getFullYear()}-${String(val.getMonth() + 1).padStart(2, '0')}-${String(val.getDate()).padStart(2, '0')}`;
    }
    const str = String(val).trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str;
    const match = str.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
    if (match) return `${match[3]}-${match[1].padStart(2, '0')}-${match[2].padStart(2, '0')}`;
    return str;
  };

  // Parse the predecessors cell. Format:
  //   TASK_CODE[:DEP_TYPE[:LAG_DAYS]]; TASK_CODE; ...
  // Returns an array of { task_code, dependency_type, lag_days }. Throws
  // a row-scoped error on invalid dep_type so the import fails fast at
  // the parse step instead of getting a less helpful server-side error.
  const parsePredecessorsCell = (cell, rowLabel) => {
    if (cell == null || cell === '') return [];
    const VALID = new Set(['FS', 'SS', 'FF', 'SF']);
    return String(cell)
      .split(';')
      .map(s => s.trim())
      .filter(Boolean)
      .map(entry => {
        const parts = entry.split(':').map(p => p.trim());
        const task_code = parts[0];
        const dependency_type = (parts[1] || 'FS').toUpperCase();
        const lag_days = parts[2] != null ? parseInt(parts[2], 10) || 0 : 0;
        if (!VALID.has(dependency_type)) {
          throw new Error(
            `${rowLabel}: predecessor '${entry}' has invalid dependency type '${parts[1]}' (must be FS, SS, FF, or SF)`,
          );
        }
        return { task_code, dependency_type, lag_days };
      });
  };

  // Parse the sov_links cell. Format:
  //   SOV_CODE:VALUE; SOV_CODE:VALUE; ...
  // Value is a percentage by default (50 = 50%). Prefix with $ for a
  // dollar amount (e.g., $25000). Returns an array shaped for the
  // server's bulk endpoint.
  const parseSovLinksCell = (cell, rowLabel) => {
    if (cell == null || cell === '') return [];
    return String(cell)
      .split(';')
      .map(s => s.trim())
      .filter(Boolean)
      .map(entry => {
        const colon = entry.indexOf(':');
        if (colon < 0) {
          throw new Error(
            `${rowLabel}: SOV link '${entry}' is missing a value (use SOV_CODE:PERCENT or SOV_CODE:$AMOUNT)`,
          );
        }
        const sov_code = entry.slice(0, colon).trim();
        const rawValue = entry.slice(colon + 1).trim();
        if (rawValue.startsWith('$')) {
          const amt = parseFloat(rawValue.slice(1).replace(/,/g, ''));
          if (isNaN(amt) || amt <= 0) {
            throw new Error(`${rowLabel}: SOV link '${entry}' has invalid amount`);
          }
          return { sov_code, allocation_method: 'Amount', allocation_amount: amt };
        }
        const pct = parseFloat(rawValue.replace(/%/g, ''));
        if (isNaN(pct) || pct <= 0) {
          throw new Error(`${rowLabel}: SOV link '${entry}' has invalid percent`);
        }
        return { sov_code, allocation_method: 'Percentage', allocation_percent: pct };
      });
  };

  const handleImportTaskFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      setLoading(true);
      const rows = await parseTaskFileRows(file);
      if (rows.length === 0) {
        alert('File must have headers and at least one data row');
        return;
      }

      // Normalize headers and pre-filter rows missing both task_name and
      // task_code so the dialog's denominator reflects what's actually
      // going to be sent.
      const validRows = rows
        .map(rowData => {
          const r = {};
          Object.keys(rowData).forEach(k => { r[k.toLowerCase().trim()] = rowData[k]; });
          return r;
        })
        .filter(r => r.task_name || r.task_code);

      if (validRows.length === 0) {
        alert('No importable rows found (every row was missing both task_name and task_code).');
        return;
      }

      setImportProgress({ total: validRows.length, status: 'importing' });

      // Single bulk POST instead of one request per row. Avoids the
      // 500-req/min general rate limiter that previously 429'd the
      // project page right after a large schedule upload. Server wraps
      // every row in one transaction.
      const payload = validRows.map((row, idx) => {
        const startDate = normalizeDate(row.start_date);
        const finishDate = normalizeDate(row.finish_date);
        let duration = 0;
        if (startDate && finishDate) {
          duration = Math.max(
            1,
            Math.ceil((new Date(finishDate) - new Date(startDate)) / (1000 * 60 * 60 * 24)) + 1,
          );
        }
        const cv = String(row.client_visible ?? 'true').toLowerCase();
        const mf = String(row.milestone_flag ?? 'false').toLowerCase();
        // rowLabel here uses idx+1 to match what server-side errors will
        // report ("Row N (code)") — keeps user-facing messages aligned
        // whether the failure happens in parse or in the POST handler.
        const rowLabel = `Row ${idx + 1} (${row.task_code || row.task_name})`;
        return {
          task_code: String(row.task_code || ''),
          task_name: String(row.task_name || ''),
          task_description: String(row.task_description || ''),
          building_area_phase: String(row.building_area_phase || ''),
          task_type: String(row.task_type || 'Work'),
          start_date: startDate,
          finish_date: finishDate,
          duration_days: duration,
          responsible_party: String(row.responsible_party || ''),
          client_visible: (cv === 'true' || cv === '1') ? 1 : 0,
          milestone_flag: (mf === 'true' || mf === '1') ? 1 : 0,
          notes: String(row.notes || ''),
          predecessors: parsePredecessorsCell(row.predecessors, rowLabel),
          sov_links: parseSovLinksCell(row.sov_links, rowLabel),
        };
      });

      const result = await bulkCreateTasks(projectId, payload);
      const importCount = result?.count ?? validRows.length;

      await fetchTasks();
      // Bubble up so the parent project page re-fetches its summary —
      // otherwise the header tiles that depend on task dates (Days
      // Remaining, project start/end) keep their pre-import nulls
      // until the user navigates away and back. SOVTab's bulk import
      // already does this same refresh.
      if (refreshSummary) refreshSummary();
      refreshTaskAlerts();
      setImportProgress({ total: validRows.length, count: importCount, status: 'success' });
      // No auto-dismiss — per the LoadingDialog spec, the complete
      // state IS the receipt. User clicks Done to dismiss.
    } catch (err) {
      setImportProgress(p => ({
        total: p?.total || 0,
        status: 'error',
        error: err.message || String(err),
      }));
    } finally {
      setLoading(false);
      e.target.value = '';
    }
  };

  const handleExportTasks = () => {
    const wb = XLSX.utils.book_new();
    const exportData = tasks.map(t => ({
      task_code: t.task_code, task_name: t.task_name, task_description: t.task_description,
      building_area_phase: t.building_area_phase, task_type: t.task_type,
      start_date: t.start_date, finish_date: t.finish_date, duration_days: t.duration_days,
      percent_complete: t.percent_complete || 0, status: t.status || 'Not Started',
      responsible_party: t.responsible_party,
      client_visible: t.client_visible ? 'true' : 'false',
      milestone_flag: t.milestone_flag ? 'true' : 'false',
      notes: t.notes,
    }));
    const ws = XLSX.utils.json_to_sheet(exportData);
    XLSX.utils.book_append_sheet(wb, ws, 'Tasks');
    XLSX.writeFile(wb, 'tasks_export.xlsx');
  };

  // ============ SECTION & TASK ACTIONS ============
  const handleAddSection = () => {
    setNewSectionName('');
    setSectionNameError('');
    setShowAddSectionModal(true);
  };

  const handleConfirmAddSection = async () => {
    if (creatingSection) return;
    const trimmed = (newSectionName || '').trim();
    if (!trimmed) {
      setSectionNameError('Section name is required.');
      return;
    }
    const existingSections = getExistingSections();
    const exactMatch = existingSections.find(s => s === trimmed);
    if (exactMatch) {
      setSectionNameError(`A section named "${exactMatch}" already exists.`);
      return;
    }
    const caseMatch = existingSections.find(s => s.toLowerCase() === trimmed.toLowerCase());
    if (caseMatch) {
      setSectionNameError(`A section named "${caseMatch}" already exists (names are case-insensitive).`);
      return;
    }
    try {
      setCreatingSection(true);
      await createScheduleSection({ project_id: projectId, section_name: trimmed });
      await fetchSections();
      setShowAddSectionModal(false);
      setNewSectionName('');
      setSectionNameError('');
    } catch (error) {
      console.error('Error creating section:', error);
      setSectionNameError('Failed to create section. Please try again.');
    } finally {
      setCreatingSection(false);
    }
  };

  // Delete a section from the schedule. The schedule view groups tasks
  // by `task.building_area_phase` (free text on the task itself), so
  // deleting the normalized `sections` row does NOT delete tasks. Any
  // SOV lines that point at this section via section_id will be set to
  // NULL by the FK's ON DELETE SET NULL (i.e. they become "unsectioned"
  // in CE). We surface both facts in the confirm so the user knows
  // exactly what changes.
  const handleDeleteSection = async (sectionName) => {
    const sec = savedSections.find(
      s => (s.section_name || '').toLowerCase() === (sectionName || '').toLowerCase()
    );
    if (!sec) {
      // The section row only exists when it was either created via the
      // Add-Section UI / + Add new section… inline / SOV bulk import.
      // Section names that come purely from task.building_area_phase
      // (legacy or manually-typed) have no DB row to delete — for those
      // the user would have to clear the building_area_phase on each
      // task instead. Tell them rather than silently no-op'ing.
      await confirm({
        title: 'Cannot delete this section',
        message: `"${sectionName}" only exists because at least one task has it set as their Building / Area / Phase. Reassign or clear that on the affected tasks to make it disappear from the list.`,
        confirmLabel: 'OK',
        // hideCancel via danger:false + showing only confirm — useConfirm
        // doesn't expose a cancel-less mode, so this still has a Cancel
        // button. Both buttons just close.
      });
      return;
    }
    const taskCount = (groupedTasks[sectionName] || []).length;
    const taskWarning = taskCount > 0
      ? ` This section currently shows ${taskCount} task${taskCount === 1 ? '' : 's'} — those tasks won't be deleted, they'll keep their Building / Area / Phase value and re-appear under this name until you change it on each task.`
      : '';
    const ok = await confirm({
      title: `Delete section "${sec.section_name}"?`,
      message:
        `Any SOV lines assigned to this section will become "unsectioned" in CE (their data is preserved, only the grouping is removed).${taskWarning}`,
      confirmLabel: 'Delete',
      danger: true,
    });
    if (!ok) return;
    try {
      await deleteScheduleSection(sec.id);
      // Refresh savedSections so the section disappears immediately.
      // fetchSections is the existing pattern (used by inline-add).
      await fetchSections();
    } catch (err) {
      console.error('Error deleting section:', err);
      await confirm({
        title: 'Could not delete section',
        message: err?.message || String(err),
        confirmLabel: 'OK',
      });
    }
  };

  const handleAddTaskToSection = (section) => {
    setFormData({
      task_code: '', task_name: '', task_description: '', building_area_phase: section,
      task_type: 'Labor', start_date: '', finish_date: '', duration_days: 0,
      status: 'Not Started', percent_complete: 0,
      responsible_party: '', client_visible: true, milestone_flag: false, notes: ''
    });
    setFormErrors({});
    setPendingPredecessors([]);
    setPendingSovLinks([]);
    setShowModal(true);
  };

  // ============ EDIT (PROPERTIES) MODAL ============
  // When TasksTab is rendered inside ScheduleView (the combined
  // Schedule tab), the parent passes onTaskOpen to route editing
  // through its shared TaskEditModal -- which has the full
  // <TaskSOVLinks> add/remove UI. Without delegation, double-clicking
  // a task row in the list opened TasksTab's own inline modal, which
  // only shows a preview-only SOV Links section when allocations
  // already exist (no way to add a first link). That left users
  // unable to link a task to an SOV line from the Tasks list -- they
  // had to find the matching Gantt bar and double-click that instead,
  // which is non-obvious and broke the rolup flow.
  // When onTaskOpen is provided, defer to the parent. Otherwise fall
  // back to the local inline modal (legacy / standalone use).
  const handleEditTask = (task) => {
    if (onTaskOpen) {
      onTaskOpen(task.id);
      return;
    }
    setEditingTaskId(task.id);
    setEditFormData({
      task_code: task.task_code || '', task_name: task.task_name || '',
      task_description: task.task_description || '', building_area_phase: task.building_area_phase || '',
      task_type: task.task_type || 'Labor', start_date: task.start_date || '',
      finish_date: task.finish_date || '', duration_days: task.duration_days || 0,
      responsible_party: task.responsible_party || '', status: task.status || 'Not Started',
      client_visible: !!task.client_visible, milestone_flag: !!task.milestone_flag,
      notes: task.notes || '',
    });
  };

  const handleEditFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    let newData = { [name]: type === 'checkbox' ? checked : value };
    if (name === 'start_date' && value && !editFormData.finish_date) newData.finish_date = value;
    setEditFormData(prev => ({ ...prev, ...newData }));
  };

  const editDuration = (() => {
    if (editFormData.start_date && editFormData.finish_date) {
      const s = new Date(editFormData.start_date);
      const f = new Date(editFormData.finish_date);
      return Math.max(0, Math.ceil((f - s) / (1000 * 60 * 60 * 24)) + 1);
    }
    return editFormData.duration_days || 0;
  })();

  const handleSaveEdit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (savingEdit) return;
    console.log('handleSaveEdit called', { editingTaskId, editFormData });
    // Validate section name — prevent near-duplicate names (e.g., "elementary school" vs "Elementary School")
    const originalTask = tasks.find(t => t.id === editingTaskId);
    const newSection = (editFormData.building_area_phase || '').trim();
    const originalSection = originalTask ? (originalTask.building_area_phase || '').trim() : '';
    if (newSection && newSection !== originalSection) {
      const existingSections = getExistingSections();
      // Check if the exact name already exists (this is fine — it just moves the task into that section)
      const exactMatch = existingSections.find(s => s === newSection);
      if (!exactMatch) {
        // No exact match — check for case-insensitive match which would create a confusing near-duplicate
        const caseMatch = existingSections.find(s => s.toLowerCase() === newSection.toLowerCase());
        if (caseMatch) {
          alert(`A section named "${caseMatch}" already exists. Did you mean to use that exact name? Section names must be unique (case-insensitive).`);
          return;
        }
      }
    }
    // Enforce: if task is scheduled entirely in the future, completion must be 0%
    // This protects billing integrity — can't bill for work on days that haven't happened
    const today = new Date().toISOString().split('T')[0];
    const newStart = editFormData.start_date;
    const newEnd = editFormData.finish_date;
    const origTask = tasks.find(tk => tk.id === editingTaskId);
    const pct = parseFloat(origTask?.percent_complete) || 0;
    const bothDatesInFuture = newStart && newEnd && newStart > today && newEnd > today;
    console.log('[SaveEdit] Date check:', { today, newStart, newEnd, pct, bothDatesInFuture, origPct: origTask?.percent_complete });

    // Proration guard: cap progress at what the schedule allows as of today
    const maxPctForEdit = getMaxProgress({ start_date: newStart, finish_date: newEnd });
    if (pct > maxPctForEdit) {
      const cappedPct = maxPctForEdit;
      if (cappedPct === 0) {
        await confirm({
          title: 'Completion Reset Required',
          message: `This task has ${pct}% completion but is now scheduled entirely in the future (starts ${newStart}). Completion will be reset to 0% because work cannot be billed for dates that haven't occurred yet.`,
          confirmLabel: 'OK',
          danger: false,
        });
      } else {
        await confirm({
          title: 'Progress Will Be Capped',
          message: `This task is at ${pct}% but the schedule (${newStart} – ${newEnd}) only supports ${cappedPct}% based on today's date. Progress will be reduced to ${cappedPct}%.`,
          confirmLabel: 'OK',
          danger: false,
        });
      }
    }

    try {
      setSavingEdit(true);
      const saveData = {
        ...editFormData,
        duration_days: editDuration,
        client_visible: editFormData.client_visible ? 1 : 0,
        milestone_flag: editFormData.milestone_flag ? 1 : 0,
      };
      // Enforce proration: cap percent_complete at the schedule-prorated max
      if (pct > maxPctForEdit) {
        saveData.percent_complete = maxPctForEdit;
        saveData.status = maxPctForEdit === 0 ? 'Not Started' : maxPctForEdit >= 100 ? 'Complete' : 'In Progress';
      }
      console.log('[SaveEdit] Saving:', { percent_complete: saveData.percent_complete, status: saveData.status, start: saveData.start_date, end: saveData.finish_date });
      await updateTask(editingTaskId, saveData);
      const savedTaskId = editingTaskId;
      setEditingTaskId(null);
      setEditFormData({});
      fetchTasks({ preserveScroll: true });
      refreshSummary();
      // Optimistic flag clear so the amber border drops the moment the
      // modal closes, even if the debounced server refresh is pending.
      if ((parseFloat(saveData.percent_complete) || 0) >= 100) {
        clearFlagLocally(savedTaskId);
      }
      refreshTaskAlerts();
    } catch (err) {
      console.error('Error updating task:', err);
      alert('Failed to save: ' + err.message);
    } finally {
      setSavingEdit(false);
    }
  };

  // Get SOV link names for display
  const getSOVLinkNames = (task) => {
    const allocs = taskAllocations[task.id];
    if (allocs && allocs.length > 0) {
      return allocs.map(a => a.sov_line_description || a.sov_code).join(', ');
    }
    return '';
  };

  // ============ RENDER ============
  // Six columns: drag, code, task name, type, dates, % done.
  // Status / SOV Links / SOV-link icon / edit icon were removed — double-click
  // a row to open the Task properties modal.
  // 6-column grid. Owner intentionally OMITTED — that's drill-in info
  // surfaced in the Task Properties modal, not scan-the-list info.
  // Reclaiming the column keeps the row dense with high-signal columns:
  // task name (wide), type, dates+duration, progress.
  const gridCols = '24px 60px 1fr 90px 160px 70px';

  // Dark-mode-aware row-tint helpers — same shape as BillingTab. Light
  // literals (#fef2f2 / #fffbeb / #fef3c7) glow white against the dark
  // canvas and wash out the light task-row text. Semi-transparent
  // accent overlays keep the tint subtle.
  const rowAlertBg = (severity) => {
    if (t.dark) {
      return severity === 'critical' ? 'rgba(239, 68, 68, 0.14)' : 'rgba(245, 158, 11, 0.12)';
    }
    return severity === 'critical' ? '#fef2f2' : '#fffbeb';
  };
  const rowAlertHoverBg = (severity) => {
    if (t.dark) {
      return severity === 'critical' ? 'rgba(239, 68, 68, 0.22)' : 'rgba(245, 158, 11, 0.22)';
    }
    return '#fef3c7';
  };

  return (
    <div className="tasks-tab">
      {/* Header — Schedule action bar per handoff §4. 60px tall (matches
          GanttTab's chrome for clean horizontal alignment across the
          split-panel seam). Title + count on left, search + ghost
          filter/sort + icon-only utility buttons + primary +Section /
          +Task on right. */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'nowrap', height: '60px', boxSizing: 'border-box', borderBottom: '1px solid var(--border-subtle)', padding: '0 16px' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexShrink: 0 }}>
          <span style={{ fontSize: 15.5, fontWeight: 600, color: 'var(--text-1)', letterSpacing: '-0.01em' }}>Schedule Tasks</span>
          <span className="mono" style={{ fontSize: 13, color: 'var(--text-3)' }}>{tasks.length}</span>
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'nowrap' }}>
          {/* Search field with ⌘K kbd hint */}
          <div style={{
            display: 'inline-flex', alignItems: 'center', height: 28,
            padding: '0 8px', gap: 6,
            background: 'var(--bg-surface-2)',
            border: '1px solid var(--border-subtle)',
            borderRadius: 6, minWidth: 180, maxWidth: 240,
          }}>
            <Icon name="search" size={13} style={{ color: 'var(--text-3)' }} />
            <input
              type="text"
              value={sharedSchedule?.scheduleQuery ?? ''}
              onChange={(e) => sharedSchedule?.setScheduleQuery?.(e.target.value)}
              placeholder="Search tasks…"
              style={{
                flex: 1, border: 'none', outline: 'none', background: 'transparent',
                color: 'var(--text-1)', fontFamily: 'var(--font-sans)', fontSize: 13.5, minWidth: 0,
              }}
            />
            {sharedSchedule?.scheduleQuery
              ? (
                <span
                  onClick={() => sharedSchedule?.setScheduleQuery?.('')}
                  title="Clear search"
                  style={{ cursor: 'pointer', color: 'var(--text-3)', fontSize: 15.5, lineHeight: 1 }}
                >×</span>
              )
              : (
                <kbd className="mono" style={{
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  height: 18, padding: '0 5px',
                  background: 'var(--bg-surface)',
                  border: '1px solid var(--border-subtle)',
                  borderRadius: 4, fontSize: 11.5, color: 'var(--text-3)',
                }}>⌘K</kbd>
              )
            }
          </div>
          {/* Filter / Sort ghost buttons. State + popover JSX live in
              TasksTab; filter+sort values flow up to ScheduleView via
              sharedSchedule so both this panel AND the Gantt rebuild
              their visible rows in lockstep. */}
          {(() => {
            const scheduleFilter = sharedSchedule?.scheduleFilter
              || { sections: new Set(), statuses: new Set(), types: new Set() };
            const filterCount =
              scheduleFilter.sections.size +
              scheduleFilter.statuses.size +
              scheduleFilter.types.size;
            return (
              <GhostButton
                icon="filter"
                label={filterCount > 0 ? `Filter (${filterCount})` : 'Filter'}
                onClick={(e) => {
                  const rect = e.currentTarget.getBoundingClientRect();
                  setFilterMenuAnchor(rect);
                  setFilterMenuOpen((v) => !v);
                  setSortMenuOpen(false);
                }}
              />
            );
          })()}
          {(() => {
            const sortKey = sharedSchedule?.scheduleSort || 'default';
            const active = SCHEDULE_SORT_OPTIONS.find((o) => o.key === sortKey);
            return (
              <GhostButton
                icon="sort"
                label={sortKey === 'default' ? 'Sort' : `Sort: ${active?.label || ''}`}
                onClick={(e) => {
                  const rect = e.currentTarget.getBoundingClientRect();
                  setSortMenuAnchor(rect);
                  setSortMenuOpen((v) => !v);
                  setFilterMenuOpen(false);
                }}
              />
            );
          })()}
          {/* Icon-only utility buttons (was text-labeled). Tooltips
              carry the verb so we don't lose discoverability. */}
          <IconUtilBtn name="template" label="Template" onClick={handleDownloadTaskTemplate} />
          <IconUtilBtn name="download" label="Export tasks" onClick={handleExportTasks} />
          <label
            title="Import tasks"
            aria-label="Import tasks"
            style={{
              width: 28, height: 28, borderRadius: 6, border: 'none',
              background: 'transparent', color: 'var(--text-3)',
              cursor: 'pointer', display: 'inline-flex',
              alignItems: 'center', justifyContent: 'center', padding: 0,
              transition: 'background .12s, color .12s', marginBottom: 0,
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-hover)'; e.currentTarget.style.color = 'var(--text-1)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text-3)'; }}
          >
            <Icon name="upload" size={14} />
            <input type="file" accept=".xlsx,.xls,.xlsm,.csv" onChange={handleImportTaskFile} style={{ display: 'none' }} />
          </label>
          {sharedSchedule?.openPrintModal && (
            <IconUtilBtn name="printer" label="Print schedule" onClick={sharedSchedule.openPrintModal} />
          )}
          <button className="btn btn-secondary" onClick={handleAddSection} style={{ padding: '5px 10px', fontSize: '13.5px', whiteSpace: 'nowrap' }}>+ Section</button>
          <button className="btn btn-primary" onClick={() => setShowModal(true)} style={{ padding: '5px 12px', fontSize: '13.5px', whiteSpace: 'nowrap' }}>+ Task</button>
        </div>
      </div>

      {((sharedSchedule ? sharedSchedule.loading : loading) && tasks.length === 0) ? (
        <LoadingState message="Loading tasks…" />
      ) : (
        <div>
          {/* Column headers — height 52 + 1px border = 53 total, matching the
              Gantt's stickyHeaderRow (52px child + 1px border = 53). */}
          <div style={{
            display: 'grid', gridTemplateColumns: gridCols, columnGap: '12px',
            height: '52px', boxSizing: 'content-box', alignItems: 'center', padding: '0 12px',
            background: 'var(--bg-surface-2)',
            borderBottom: '1px solid var(--border-subtle)',
            fontSize: 12, fontWeight: 500, textTransform: 'uppercase',
            color: 'var(--text-3)', letterSpacing: '0.04em',
          }}>
            <div></div>
            <div>Code</div>
            <div>Task</div>
            <div>Type</div>
            <div>Dates</div>
            <div>% Done</div>
          </div>

          {displaySections.map(section => (
            <div
              key={section}
              onDragOver={(e) => { if (draggedSectionName) { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setDragOverSectionName(section); } }}
              onDrop={(e) => { if (draggedSectionName) handleSectionDrop(e, section); }}
              style={{
                // Use inset box-shadow for the drag indicator instead of a
                // 3px transparent border — borders take up layout space even
                // when transparent, which added 3px per section of accumulated
                // drift between the Tasks list and the Gantt rows.
                boxShadow: dragOverSectionName === section && draggedSectionName && draggedSectionName !== section
                  ? 'inset 0 3px 0 0 #3b82f6'
                  : 'none',
                transition: 'box-shadow 0.15s',
              }}
            >
              {/* Section header */}
              <div
                draggable
                onDragStart={(e) => handleSectionDragStart(e, section)}
                onDragEnd={handleSectionDragEnd}
                style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  height: '40px', boxSizing: 'border-box', padding: '0 12px',
                  backgroundColor: dragOverSectionName === section && draggedSectionName && draggedSectionName !== section ? 'var(--accent-soft)' : 'var(--bg-surface-2)',
                  borderBottom: '1px solid var(--border-subtle)', cursor: 'grab',
                  transition: 'background-color 0.15s',
                }}
                onClick={() => { if (!draggedSectionName) toggleSection(section); }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, flex: 1 }}>
                  <span style={{ fontSize: 15.5, color: t.textMuted, cursor: 'grab', userSelect: 'none' }} title="Drag to reorder section">{'⣹'}</span>
                  <span style={{ fontSize: 13.5, color: 'var(--text-3)' }}>
                    {collapsedSections[section] ? '▶' : '▼'}
                  </span>
                  {/* 6px section status dot — grey by default, green
                      when every task in the section is 100% complete.
                      Matches the right-panel roll-up bar's color. */}
                  {(() => {
                    const list = displayGroupedTasks[section] || [];
                    const allComplete = list.length > 0
                      && list.every(x => (parseFloat(x.percent_complete ?? x.progress) || 0) >= 100);
                    return (
                      <span
                        aria-hidden
                        title={allComplete ? 'All tasks complete' : 'In progress'}
                        style={{
                          width: 6, height: 6, borderRadius: 999,
                          background: allComplete ? 'var(--success)' : 'var(--text-3)',
                          flexShrink: 0,
                        }}
                      />
                    );
                  })()}
                  <span style={{ fontWeight: 600, fontSize: 13.5, letterSpacing: '-0.01em', color: 'var(--text-1)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {section}
                  </span>
                  <span className="mono" style={{ color: 'var(--text-3)', fontSize: 12.5, fontWeight: 500, flexShrink: 0 }}>
                    {(displayGroupedTasks[section] || []).length}
                  </span>
                  {(() => {
                    const st = (displayGroupedTasks[section] || []).filter(x => x.start_date && x.finish_date);
                    if (st.length === 0) return null;
                    let minS = st[0].start_date;
                    let maxE = st[0].finish_date;
                    st.forEach(x => {
                      if (x.start_date < minS) minS = x.start_date;
                      if (x.finish_date > maxE) maxE = x.finish_date;
                    });
                    return (
                      <span className="mono" style={{
                        marginLeft: 'auto',
                        marginRight: 8,
                        fontSize: 12.5,
                        color: 'var(--text-3)',
                        letterSpacing: '-0.01em',
                        whiteSpace: 'nowrap',
                        flexShrink: 0,
                      }}>
                        {fmtCompactDate(minS)} <span style={{ color: 'var(--text-4)' }}>{'→'}</span> {fmtCompactDate(maxE)}
                      </span>
                    );
                  })()}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <button
                    className="btn btn-secondary"
                    onClick={(e) => { e.stopPropagation(); handleAddTaskToSection(section); }}
                    style={{ fontSize: '13.5px', padding: '4px 12px' }}
                  >
                    + Add Task
                  </button>
                  {/* Delete-section control. Subtle '×' so it doesn't compete
                      visually with + Add Task, but discoverable. Confirms
                      first (handleDeleteSection); only the normalized
                      sections-table row is removed — tasks stay put. */}
                  <button
                    type="button"
                    aria-label={`Delete section ${section}`}
                    title="Delete section"
                    onClick={(e) => { e.stopPropagation(); handleDeleteSection(section); }}
                    style={{
                      background: 'transparent',
                      border: `1px solid ${t.cardBorder}`,
                      color: t.textMuted,
                      borderRadius: '6px',
                      width: '26px',
                      height: '26px',
                      cursor: 'pointer',
                      fontSize: '15.5px',
                      lineHeight: 1,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: 0,
                      transition: 'color 0.15s, border-color 0.15s, background-color 0.15s',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.color = '#ef4444';
                      e.currentTarget.style.borderColor = '#ef4444';
                      e.currentTarget.style.backgroundColor = 'rgba(239,68,68,0.08)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.color = t.textMuted;
                      e.currentTarget.style.borderColor = t.cardBorder;
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    ×
                  </button>
                </div>
              </div>

              {/* Empty section placeholder */}
              {!collapsedSections[section] && (!displayGroupedTasks[section] || displayGroupedTasks[section].length === 0) && (
                <div style={{ padding: '20px 12px', textAlign: 'center', color: t.textMuted, fontSize: '14.5px', borderBottom: '1px solid var(--border-subtle)' }}>
                  No tasks yet — click "+ Add Task" above to get started
                </div>
              )}
              {/* Task rows */}
              {!collapsedSections[section] && (displayGroupedTasks[section] || []).map((task, idx) => {
                const pct = task.percent_complete || 0;
                const hasAllocations = taskAllocations[task.id] && taskAllocations[task.id].length > 0;
                const duration = task.duration_days || 0;
                const validPredecessors = (task.predecessors || []).filter(p => p.predecessor_task_id != null);
                const hasPredecessors = validPredecessors.length > 0;
                const successorTasks = tasks.filter(t => (t.predecessors || []).some(p => p.predecessor_task_id != null && Number(p.predecessor_task_id) === Number(task.id)));
                const isPrereqForOthers = successorTasks.length > 0;
                const isDragOver = dragOverTask === task.id;
                const depFlagged = (depAlertData.flagged_task_ids || []).includes(task.id);
                const taskDepAlerts = depFlagged ? (depAlertData.alerts || []).filter(a => a.task_id === task.id || a.related_task_id === task.id) : [];

                return (
                  <React.Fragment key={task.id}>
                    <div
                      draggable
                      onDragStart={(e) => handleDragStart(e, task, section)}
                      onDragEnd={handleDragEnd}
                      onDragOver={(e) => handleDragOver(e, task)}
                      onDrop={(e) => handleDrop(e, task, section)}
                      onDoubleClick={() => handleEditTask(task)}
                      style={{
                        display: 'grid', gridTemplateColumns: gridCols, columnGap: '12px',
                        height: '40px', boxSizing: 'border-box', padding: '0 12px', alignItems: 'center', fontSize: '14.5px',
                        borderBottom: '1px solid var(--border-subtle)',
                        borderTop: isDragOver ? '2px solid #3b82f6' : 'none',
                        borderLeft: depFlagged ? (taskDepAlerts.some(a => a.severity === 'critical') ? '3px solid #dc2626' : '3px solid #f59e0b') : flaggedTaskIds.has(task.id) ? '3px solid #f59e0b' : '3px solid transparent',
                        backgroundColor: depFlagged
                          ? rowAlertBg(taskDepAlerts.some(a => a.severity === 'critical') ? 'critical' : 'warning')
                          : flaggedTaskIds.has(task.id)
                            ? rowAlertBg('warning')
                            : 'transparent',
                        transition: 'background-color 0.15s',
                        cursor: 'default',
                        userSelect: 'none',
                      }}
                      onMouseEnter={(e) => {
                        const sev = depFlagged
                          ? (taskDepAlerts.some(a => a.severity === 'critical') ? 'critical' : 'warning')
                          : (flaggedTaskIds.has(task.id) ? 'warning' : null);
                        e.currentTarget.style.backgroundColor = sev
                          ? rowAlertHoverBg(sev)
                          : (t.dark ? 'rgba(255,255,255,0.03)' : '#f8fafc');
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = depFlagged
                          ? rowAlertBg(taskDepAlerts.some(a => a.severity === 'critical') ? 'critical' : 'warning')
                          : flaggedTaskIds.has(task.id)
                            ? rowAlertBg('warning')
                            : 'transparent';
                      }}
                    >
                      {/* Drag handle - 6 dot grip */}
                      <div style={{ cursor: 'grab', userSelect: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2px 0' }}>
                        <svg width="10" height="16" viewBox="0 0 10 16" fill="none">
                          <circle cx="3" cy="2" r="1.3" fill="#cbd5e1" />
                          <circle cx="7" cy="2" r="1.3" fill="#cbd5e1" />
                          <circle cx="3" cy="8" r="1.3" fill="#cbd5e1" />
                          <circle cx="7" cy="8" r="1.3" fill="#cbd5e1" />
                          <circle cx="3" cy="14" r="1.3" fill="#cbd5e1" />
                          <circle cx="7" cy="14" r="1.3" fill="#cbd5e1" />
                        </svg>
                      </div>

                      {/* Code (redesign: mono, text-4, smaller — was 12px t.textSecondary) */}
                      <div className="mono" style={{ color: 'var(--text-4)', fontWeight: 500, fontSize: '12.5px', letterSpacing: '-0.02em' }}>{task.task_code}</div>

                      {/* Task name + link indicators */}
                      <div style={{ fontWeight: 500, display: 'flex', alignItems: 'center', gap: '6px', color: t.heading, minWidth: 0 }}>
                        <span title={task.task_name} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>{task.task_name}</span>
                        {depFlagged && (
                          <span
                            title={taskDepAlerts.map(a => a.message).join('\n') || 'Scheduling alert'}
                            style={{
                              fontSize: '14.5px',
                              cursor: 'help',
                              color: taskDepAlerts.some(a => a.severity === 'critical') ? '#dc2626' : '#d97706',
                            }}
                          >&#9888;</span>
                        )}
                        {isPrereqForOthers && (
                          <span className="dep-tooltip-wrap" style={{ display: 'inline-flex', alignItems: 'center', position: 'relative' }}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#8b5cf6" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ cursor: 'pointer' }}>
                              <line x1="5" y1="12" x2="19" y2="12" />
                              <polyline points="12 5 19 12 12 19" />
                            </svg>
                            <div className="dep-tooltip" style={{
                              display: 'none', position: 'absolute', bottom: '100%', left: '50%', transform: 'translateX(-50%)',
                              marginBottom: '6px', padding: '8px 12px', backgroundColor: '#1e293b', color: '#f8fafc',
                              borderRadius: '8px', fontSize: '13.5px', whiteSpace: 'nowrap', zIndex: 1000,
                              boxShadow: '0 4px 12px rgba(0,0,0,0.15)', pointerEvents: 'none',
                            }}>
                              <div style={{ fontWeight: 600, color: '#c4b5fd', marginBottom: successorTasks.length > 1 ? '4px' : '0' }}>Dependents:</div>
                              {successorTasks.map(s => (
                                <div key={s.id} style={{ padding: '1px 0' }}>
                                  <span style={{ color: '#c4b5fd', fontWeight: 600 }}>{s.task_code}</span>
                                  <span style={{ color: t.cardBorder }}> {s.task_name}</span>
                                </div>
                              ))}
                              <div style={{ position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)',
                                width: 0, height: 0, borderLeft: '6px solid transparent', borderRight: '6px solid transparent', borderTop: '6px solid #1e293b' }} />
                            </div>
                          </span>
                        )}
                        {hasPredecessors && (
                          <span className="dep-tooltip-wrap" style={{ display: 'inline-flex', alignItems: 'center', position: 'relative' }}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ cursor: 'pointer' }}>
                              <line x1="19" y1="12" x2="5" y2="12" />
                              <polyline points="12 19 5 12 12 5" />
                            </svg>
                            <div className="dep-tooltip" style={{
                              display: 'none', position: 'absolute', bottom: '100%', left: '50%', transform: 'translateX(-50%)',
                              marginBottom: '6px', padding: '8px 12px', backgroundColor: '#1e293b', color: '#f8fafc',
                              borderRadius: '8px', fontSize: '13.5px', whiteSpace: 'nowrap', zIndex: 1000,
                              boxShadow: '0 4px 12px rgba(0,0,0,0.15)', pointerEvents: 'none',
                            }}>
                              <div style={{ fontWeight: 600, color: '#6ee7b7', marginBottom: validPredecessors.length > 1 ? '4px' : '0' }}>Predecessors:</div>
                              {validPredecessors.map(p => {
                                const pt = tasks.find(t => Number(t.id) === Number(p.predecessor_task_id));
                                return (
                                  <div key={p.id || p.predecessor_task_id} style={{ padding: '1px 0' }}>
                                    <span style={{ color: '#6ee7b7', fontWeight: 600 }}>{pt ? pt.task_code : '?'}</span>
                                    <span style={{ color: '#e2e8f0' }}> {pt ? pt.task_name : 'Unknown'}</span>
                                  </div>
                                );
                              })}
                              <div style={{ position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)',
                                width: 0, height: 0, borderLeft: '6px solid transparent', borderRight: '6px solid transparent', borderTop: '6px solid #1e293b' }} />
                            </div>
                          </span>
                        )}
                        {/* SOV link affordance — only shown when the
                            task actually has at least one SOV link, so
                            unlinked rows don't get a button that looks
                            like it does nothing useful. Click opens the
                            properties modal where the link can be
                            edited / removed via <TaskSOVLinks>. To add
                            a FIRST link, the user double-clicks the row
                            (no icon shortcut for that path -- the empty
                            state is the unlinked row itself).
                            stopPropagation so the click doesn't also
                            fire the row's onDoubleClick. */}
                        {hasAllocations && (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); handleEditTask(task); }}
                            title={`${taskAllocations[task.id].length} SOV link${taskAllocations[task.id].length === 1 ? '' : 's'} — click to edit`}
                            style={{
                              background: 'none', border: 'none', padding: 0,
                              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                              cursor: 'pointer', borderRadius: 4,
                            }}
                          >
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                              <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                            </svg>
                          </button>
                        )}
                      </div>

                      {/* Type — redesigned as a 7px leading dot in the type's
                          accent color (was a full pastel pill). Tooltip carries
                          the type name so we don't lose information. */}
                      <div title={task.task_type} style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                        <span
                          aria-hidden
                          style={{
                            width: 7, height: 7, borderRadius: 999,
                            background: getTaskTypeColorVar(task.task_type),
                            boxShadow: `0 0 0 2px color-mix(in oklch, ${getTaskTypeColorVar(task.task_type)} 15%, transparent)`,
                            flexShrink: 0,
                          }}
                        />
                        <span style={{ fontSize: 12, color: 'var(--text-4)', letterSpacing: '0.02em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {task.task_type}
                        </span>
                      </div>

                      {/* Dates + duration — two-line stack per Variant B
                          spec (date range on top, duration below). */}
                      <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.15 }}>
                        <div className="mono" style={{ fontSize: 12.5, color: 'var(--text-2)', whiteSpace: 'nowrap', letterSpacing: '-0.01em' }}>
                          <span>{fmtCompactDate(task.start_date)}</span>
                          <span style={{ color: 'var(--text-4)', margin: '0 4px' }}>{'→'}</span>
                          <span>{fmtCompactDate(task.finish_date)}</span>
                        </div>
                        <div className="mono" style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 2, letterSpacing: '-0.005em' }}>
                          {duration} day{duration === 1 ? '' : 's'}
                        </div>
                      </div>

                      {/* % Done with progress bar */}
                      <div
                        style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                        onDoubleClick={(e) => {
                          e.stopPropagation();
                          handlePercentCompleteDoubleClick(task.id, pct);
                        }}
                      >
                        {editingPercentCompleteId === task.id ? (
                          <input
                            type="number" min="0" max="100"
                            value={editingPercentCompleteValue}
                            onChange={(e) => setEditingPercentCompleteValue(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handlePercentCompleteEditSave(task.id);
                              else if (e.key === 'Escape') handlePercentCompleteEditCancel();
                            }}
                            onBlur={() => handlePercentCompleteEditSave(task.id)}
                            autoFocus
                            onClick={(e) => e.stopPropagation()}
                            style={{ width: '50px', fontSize: '13.5px', padding: '2px 4px', border: '1px solid #3b82f6', borderRadius: '4px', outline: 'none' }}
                          />
                        ) : (
                          <>
                            <div style={{
                              width: '50px', height: '4px',
                              background: 'var(--border-subtle)',
                              borderRadius: 999, overflow: 'hidden',
                            }}>
                              <div style={{
                                width: `${Math.min(pct, 100)}%`, height: '100%',
                                background: pct >= 100 ? 'var(--success)' : 'var(--accent)',
                                opacity: pct === 0 ? 0.3 : 1,
                                borderRadius: 999, transition: 'width 0.3s',
                              }} />
                            </div>
                            <span className="mono" style={{ fontSize: '12.5px', color: pct === 0 ? 'var(--text-4)' : 'var(--text-2)', fontWeight: 500 }}>
                              {pct}%
                            </span>
                          </>
                        )}
                      </div>

                    </div>

                    {/* SOV Link modal rendered at bottom of component */}
                  </React.Fragment>
                );
              })}
            </div>
          ))}
        </div>
      )}

      {/* Add Section Modal */}
      {showAddSectionModal && (
        <div className="modal-overlay" onClick={() => setShowAddSectionModal(false)}>
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: t.bg, borderRadius: '12px', width: '420px', maxWidth: '90vw',
              boxShadow: '0 20px 60px rgba(0,0,0,0.15)', animation: 'slideUp 0.2s ease',
              overflow: 'hidden',
            }}
          >
            {/* Header */}
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '18px 24px', borderBottom: `1px solid ${t.cardBorder}`,
            }}>
              <h3 style={{ margin: 0, fontSize: '17.5px', fontWeight: 600 }}>Add New Section</h3>
              <button
                onClick={() => setShowAddSectionModal(false)}
                style={{
                  background: 'none', border: 'none', fontSize: '21.5px', color: t.textMuted,
                  cursor: 'pointer', padding: '0 4px', lineHeight: 1,
                }}
              >&times;</button>
            </div>
            {/* Body */}
            <div style={{ padding: '24px' }}>
              <label style={{ display: 'block', fontSize: '14.5px', fontWeight: 600, color: t.text, marginBottom: '6px' }}>
                Section Name (Building Area / Phase)
              </label>
              <input
                type="text"
                autoFocus
                value={newSectionName}
                onChange={(e) => { setNewSectionName(e.target.value); setSectionNameError(''); }}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleConfirmAddSection(); } }}
                placeholder="e.g. Elementary School, High School Wing B"
                style={{
                  width: '100%', padding: '10px 12px', fontSize: '15.5px',
                  border: sectionNameError ? '1px solid #ef4444' : `1px solid ${t.cardBorder}`,
                  borderRadius: '8px', outline: 'none', boxSizing: 'border-box',
                  transition: 'border-color 0.15s',
                }}
                onFocus={(e) => { if (!sectionNameError) e.target.style.borderColor = '#3b82f6'; }}
                onBlur={(e) => { if (!sectionNameError) e.target.style.borderColor = t.cardBorder; }}
              />
              {sectionNameError && (
                <div style={{ color: '#ef4444', fontSize: '13.5px', marginTop: '6px' }}>{sectionNameError}</div>
              )}
            </div>
            {/* Footer */}
            <div style={{
              display: 'flex', justifyContent: 'flex-end', gap: '10px',
              padding: '16px 24px', borderTop: '1px solid var(--border-subtle)',
            }}>
              <button
                onClick={() => setShowAddSectionModal(false)}
                disabled={creatingSection}
                style={{
                  padding: '8px 20px', fontSize: '14.5px', fontWeight: 500,
                  border: `1px solid ${t.cardBorder}`, borderRadius: '8px', background: t.bg,
                  color: t.text,
                  cursor: creatingSection ? 'not-allowed' : 'pointer',
                  opacity: creatingSection ? 0.6 : 1,
                }}
              >Cancel</button>
              <button
                onClick={handleConfirmAddSection}
                disabled={creatingSection}
                style={{
                  padding: '8px 20px', fontSize: '14.5px', fontWeight: 600,
                  border: 'none', borderRadius: '8px', background: '#3b82f6',
                  color: '#fff',
                  cursor: creatingSection ? 'wait' : 'pointer',
                  opacity: creatingSection ? 0.8 : 1,
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                }}
              >
                {creatingSection && (
                  <span style={{
                    width: 14, height: 14, border: '2px solid rgba(255,255,255,0.4)',
                    borderTopColor: '#fff', borderRadius: '50%',
                    animation: 'spin 0.7s linear infinite', display: 'inline-block',
                  }} />
                )}
                {creatingSection ? 'Creating…' : 'Create Section'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Task import — themed LoadingDialog. Same shared primitive as
          SOVTab. Stays mounted across importing → success / error so
          the bar animates smoothly. */}
      <LoadingDialog
        open={!!importProgress}
        state={
          importProgress?.status === 'success' ? 'complete'
            : importProgress?.status === 'error' ? 'error'
            : 'progress'
        }
        noun="tasks"
        current={importProgress?.count || 0}
        total={importProgress?.total || 0}
        errorMessage={importProgress?.error}
        cancellable={false}
        onDismiss={() => setImportProgress(null)}
        onRetry={() => setImportProgress(null)}
      />

      {/* Add Task Modal */}
      {showModal && (() => {
        // Warm-cream + sage tokens, matching the Task Properties (TaskEditModal)
        // design so Add + Edit feel like one component.
        const atInputStyle = {
          width: '100%', height: 34, padding: '0 10px', background: 'var(--bg-surface)',
          border: '1px solid var(--border-default)', borderRadius: 8, fontSize: 14.5,
          color: 'var(--text-1)', letterSpacing: '-0.005em', outline: 'none',
          boxSizing: 'border-box', fontFamily: 'inherit',
        };
        const atTextareaStyle = { ...atInputStyle, height: 'auto', minHeight: 56, padding: '8px 10px', resize: 'vertical', lineHeight: 1.45 };
        const atLabelStyle = { display: 'block', fontSize: 12, fontWeight: 500, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 };
        const atFieldGroup = { marginBottom: 12 };
        const handleFocus = (e) => { e.currentTarget.style.borderColor = 'var(--accent)'; e.currentTarget.style.boxShadow = '0 0 0 2px var(--accent-ring)'; };
        const handleBlur = (e) => { e.currentTarget.style.borderColor = 'var(--border-default)'; e.currentTarget.style.boxShadow = 'none'; };
        const errText = (msg) => <div style={{ color: 'var(--danger)', fontSize: 12.5, marginTop: 4 }}>{msg}</div>;
        const closeModal = () => {
          setShowModal(false); setFormErrors({}); setPendingPredecessors([]); setPendingSovLinks([]);
          // Reset the form too, so a cancelled draft (incl. a Status=Complete
          // pick) can't silently pre-fill the next "+ Task" open.
          setFormData({
            task_code: '', task_name: '', task_description: '', building_area_phase: '',
            task_type: 'Labor', start_date: '', finish_date: '', duration_days: 0,
            status: 'Not Started', percent_complete: 0,
            responsible_party: '', client_visible: true, milestone_flag: false, notes: '',
          });
        };
        const sectionHead = (title, first) => (
          <div style={{ display: 'flex', alignItems: 'center', padding: first ? '2px 0 10px' : '20px 0 10px', borderBottom: '1px solid var(--border-subtle)', marginBottom: 12 }}>
            <span style={{ fontSize: 12.5, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-3)' }}>{title}</span>
          </div>
        );
        const TYPE_COLOR = { Labor: 'var(--type-labor)', Material: 'var(--type-material)', Procurement: 'var(--type-procurement)', Engineering: 'var(--type-engineering)', Inspection: 'var(--type-inspection)', Milestone: 'var(--type-milestone)' };
        const STATUS_META = {
          'Not Started': { color: 'var(--text-3)', bg: 'var(--bg-surface-2)' },
          'In Progress': { color: 'var(--accent-text)', bg: 'var(--accent-soft)' },
          'Complete': { color: 'var(--success)', bg: 'var(--success-soft)' },
        };
        const pill = (color, bg, label) => (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '2px 8px', borderRadius: 999, fontSize: 12.5, fontWeight: 500, color, background: bg, border: `1px solid color-mix(in oklch, ${color} 22%, transparent)`, whiteSpace: 'nowrap' }}>
            <span style={{ width: 5, height: 5, borderRadius: 99, background: color }} />{label}
          </span>
        );
        const typeColor = TYPE_COLOR[formData.task_type] || 'var(--text-3)';
        const stMeta = STATUS_META[formData.status] || STATUS_META['Not Started'];
        return (
          <div onClick={closeModal} style={{
            position: 'fixed', inset: 0, zIndex: 10000,
            background: 'oklch(0.3 0.02 90 / 0.42)', backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)',
            display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: 'max(28px, 5vh)',
          }}>
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                width: 580, maxWidth: 'calc(100vw - 32px)', maxHeight: 'calc(100vh - 56px)',
                display: 'flex', flexDirection: 'column', background: 'var(--bg-surface)',
                borderRadius: 16, border: '1px solid var(--border-subtle)',
                boxShadow: '0 20px 50px -10px oklch(0.2 0.02 90 / 0.25), 0 8px 20px oklch(0.2 0.02 90 / 0.08)',
                overflow: 'hidden',
              }}
            >
              {/* Header */}
              <div style={{ padding: '18px 24px 16px', borderBottom: '1px solid var(--border-subtle)', flexShrink: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  {pill(typeColor, `color-mix(in oklch, ${typeColor} 12%, transparent)`, formData.task_type || 'Labor')}
                  {pill(stMeta.color, stMeta.bg, formData.status || 'Not Started')}
                  <div style={{ flex: 1 }} />
                  <button type="button" onClick={closeModal} aria-label="Close" style={{
                    width: 30, height: 30, borderRadius: 7, background: 'transparent', border: 'none',
                    color: 'var(--text-3)', fontSize: 22, lineHeight: 1, cursor: 'pointer',
                  }}>&times;</button>
                </div>
                <h2 style={{ margin: 0, fontSize: 19.5, fontWeight: 600, color: 'var(--text-1)', letterSpacing: '-0.018em' }}>Add New Task</h2>
              </div>
              {/* Body */}
              <div style={{ flex: 1, overflowY: 'auto', padding: '14px 24px 8px' }}>
                {sectionHead('General', true)}
                {/* Task Name */}
                <div style={atFieldGroup}>
                  <label style={atLabelStyle}>Task Name <span style={{ color: 'var(--danger)' }}>*</span></label>
                  <input type="text" name="task_name" value={formData.task_name} onChange={handleFormChange}
                    style={{ ...atInputStyle, borderColor: formErrors.task_name ? 'var(--danger)' : 'var(--border-default)' }}
                    onFocus={handleFocus} onBlur={handleBlur}
                    placeholder="Enter task name" />
                  {formErrors.task_name && errText(formErrors.task_name)}
                </div>
                {/* Description */}
                <div style={atFieldGroup}>
                  <label style={atLabelStyle}>Description</label>
                  <textarea name="task_description" value={formData.task_description} onChange={handleFormChange}
                    rows="2" placeholder="Describe the scope of this task…"
                    style={atTextareaStyle}
                    onFocus={handleFocus} onBlur={handleBlur} />
                </div>
                {/* Section + Type row */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, ...atFieldGroup }}>
                  <div>
                    <label style={atLabelStyle}>Section <span style={{ color: 'var(--danger)' }}>*</span></label>
                    {formData._addingNewSection ? (
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        <input
                          type="text"
                          placeholder="New section name..."
                          value={formData._newSectionName || ''}
                          autoFocus
                          onChange={e => setFormData(f => ({ ...f, _newSectionName: e.target.value }))}
                          onKeyDown={e => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              const name = (formData._newSectionName || '').trim();
                              if (name) {
                                createScheduleSection({ project_id: projectId, section_name: name })
                                  .then(() => fetchSections())
                                  .catch(() => {});
                                setFormData(f => ({ ...f, building_area_phase: name, _addingNewSection: false, _newSectionName: '' }));
                              }
                            } else if (e.key === 'Escape') {
                              setFormData(f => ({ ...f, _addingNewSection: false, _newSectionName: '' }));
                            }
                          }}
                          style={{ ...atInputStyle, flex: 1, borderColor: formErrors.building_area_phase ? 'var(--danger)' : 'var(--border-default)' }}
                        />
                        <button
                          type="button"
                          onClick={() => {
                            const name = (formData._newSectionName || '').trim();
                            if (name) {
                              createScheduleSection({ project_id: projectId, section_name: name })
                                .then(() => fetchSections())
                                .catch(() => {});
                              setFormData(f => ({ ...f, building_area_phase: name, _addingNewSection: false, _newSectionName: '' }));
                            }
                          }}
                          style={{
                            padding: '0 12px', height: 34, fontSize: 13.5, fontWeight: 500, cursor: 'pointer',
                            border: '1px solid color-mix(in oklch, var(--accent) 50%, black)', borderRadius: 8,
                            background: 'var(--accent)', color: '#fff', whiteSpace: 'nowrap',
                          }}
                        >Add</button>
                        <button
                          type="button"
                          onClick={() => setFormData(f => ({ ...f, _addingNewSection: false, _newSectionName: '' }))}
                          style={{
                            padding: '0 10px', height: 34, fontSize: 13.5, cursor: 'pointer',
                            border: '1px solid var(--border-default)', borderRadius: 8, background: 'var(--bg-surface)', color: 'var(--text-3)',
                          }}
                        >Cancel</button>
                      </div>
                    ) : (
                      <select name="building_area_phase" value={formData.building_area_phase} onChange={e => {
                          if (e.target.value === '__new__') {
                            setFormData(f => ({ ...f, _addingNewSection: true, _newSectionName: '' }));
                          } else {
                            handleFormChange(e);
                          }
                        }}
                        style={{ ...atInputStyle, cursor: 'pointer', borderColor: formErrors.building_area_phase ? 'var(--danger)' : 'var(--border-default)' }}
                        onFocus={handleFocus} onBlur={handleBlur}>
                        <option value="">— Select Section —</option>
                        {getExistingSections().sort().map(sec => (
                          <option key={sec} value={sec}>{sec}</option>
                        ))}
                        <option value="__new__" style={{ fontWeight: 600 }}>+ Add New Section</option>
                      </select>
                    )}
                    {formErrors.building_area_phase && errText(formErrors.building_area_phase)}
                  </div>
                  <div>
                    <label style={atLabelStyle}>Task Type</label>
                    <select name="task_type" value={formData.task_type} onChange={handleFormChange}
                      style={{ ...atInputStyle, cursor: 'pointer' }} onFocus={handleFocus} onBlur={handleBlur}>
                      <option>Labor</option>
                      <option>Material</option>
                      <option>Procurement</option>
                      <option>Engineering</option>
                      <option>Inspection</option>
                      <option>Milestone</option>
                    </select>
                  </div>
                </div>
                {/* Status + Responsible Party row */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, ...atFieldGroup }}>
                  <div>
                    <label style={atLabelStyle}>Status</label>
                    <select name="status" value={formData.status || 'Not Started'}
                      onChange={(e) => {
                        const status = e.target.value;
                        let pct = parseFloat(formData.percent_complete) || 0;
                        if (status === 'Complete' && pct < 100) pct = 100;
                        else if (status === 'Not Started' && pct > 0) pct = 0;
                        else if (status === 'In Progress' && (pct === 0 || pct === 100)) pct = 50;
                        setFormData(f => ({ ...f, status, percent_complete: pct }));
                      }}
                      style={{ ...atInputStyle, cursor: 'pointer' }} onFocus={handleFocus} onBlur={handleBlur}>
                      <option>Not Started</option>
                      <option>In Progress</option>
                      <option>Complete</option>
                    </select>
                  </div>
                  <div>
                    <label style={atLabelStyle}>Responsible Party</label>
                    <input type="text" name="responsible_party" value={formData.responsible_party} onChange={handleFormChange}
                      style={atInputStyle} onFocus={handleFocus} onBlur={handleBlur}
                      placeholder="Crew or vendor" />
                  </div>
                </div>

                {/* SCHEDULE */}
                {sectionHead('Schedule')}
                {(() => {
                  // Compute earliest allowed dates from pending predecessors
                  let earliestStart = null;
                  let earliestFinish = null;
                  let startHint = '';
                  let finishHint = '';
                  const addDays = (dateStr, days) => {
                    const d = new Date(dateStr + 'T00:00:00');
                    d.setDate(d.getDate() + days);
                    return d;
                  };
                  const fmtISO = (d) => d.toISOString().split('T')[0];
                  const fmtDisplay = (d) => d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });

                  for (const pred of pendingPredecessors) {
                    const pt = tasks.find(t => Number(t.id) === Number(pred.predecessor_task_id));
                    if (!pt) continue;
                    const lag = pred.lag_days || 0;
                    switch (pred.dependency_type) {
                      case 'FS':
                        if (pt.finish_date) {
                          const d = addDays(pt.finish_date, lag);
                          if (!earliestStart || d > earliestStart) { earliestStart = d; startHint = `Earliest: ${fmtDisplay(d)} (after ${pt.task_code} finishes)`; }
                        }
                        break;
                      case 'SS':
                        if (pt.start_date) {
                          const d = addDays(pt.start_date, lag);
                          if (!earliestStart || d > earliestStart) { earliestStart = d; startHint = `Earliest: ${fmtDisplay(d)} (with ${pt.task_code} start)`; }
                        }
                        break;
                      case 'FF':
                        if (pt.finish_date) {
                          const d = addDays(pt.finish_date, lag);
                          if (!earliestFinish || d > earliestFinish) { earliestFinish = d; finishHint = `Earliest: ${fmtDisplay(d)} (with ${pt.task_code} finish)`; }
                        }
                        break;
                      case 'SF':
                        if (pt.start_date) {
                          const d = addDays(pt.start_date, lag);
                          if (!earliestFinish || d > earliestFinish) { earliestFinish = d; finishHint = `Earliest: ${fmtDisplay(d)} (after ${pt.task_code} starts)`; }
                        }
                        break;
                      default: break;
                    }
                  }
                  const startMin = earliestStart ? fmtISO(earliestStart) : undefined;
                  const finishMinFromPred = earliestFinish ? fmtISO(earliestFinish) : undefined;
                  const finishMin = [formData.start_date, finishMinFromPred].filter(Boolean).sort().pop() || undefined;
                  const durDays = (formData.start_date && formData.finish_date)
                    ? Math.max(1, Math.ceil((new Date(formData.finish_date) - new Date(formData.start_date)) / 86400000) + 1)
                    : (formData.duration_days || 0);

                  return (
                    <div style={atFieldGroup}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 110px', gap: 12 }}>
                        <div>
                          <label style={atLabelStyle}>Start Date <span style={{ color: 'var(--danger)' }}>*</span></label>
                          <input type="date" name="start_date" value={formData.start_date} onChange={handleFormChange}
                            min={startMin}
                            style={{ ...atInputStyle, fontFamily: 'var(--font-mono)', letterSpacing: '-0.02em', borderColor: formErrors.start_date ? 'var(--danger)' : 'var(--border-default)' }}
                            onFocus={handleFocus} onBlur={handleBlur} />
                          {formErrors.start_date && errText(formErrors.start_date)}
                        </div>
                        <div>
                          <label style={atLabelStyle}>Finish Date <span style={{ color: 'var(--danger)' }}>*</span></label>
                          <input type="date" name="finish_date" value={formData.finish_date} onChange={handleFormChange}
                            min={finishMin}
                            style={{ ...atInputStyle, fontFamily: 'var(--font-mono)', letterSpacing: '-0.02em', borderColor: formErrors.finish_date ? 'var(--danger)' : 'var(--border-default)' }}
                            onFocus={handleFocus} onBlur={handleBlur} />
                          {formErrors.finish_date && errText(formErrors.finish_date)}
                        </div>
                        <div>
                          <label style={atLabelStyle}>Duration</label>
                          <div style={{ ...atInputStyle, display: 'flex', alignItems: 'center', background: 'var(--bg-surface-2)', border: '1px dashed var(--border-default)', color: 'var(--text-3)', fontFamily: 'var(--font-mono)', letterSpacing: '-0.02em' }}>
                            {durDays} days
                          </div>
                        </div>
                      </div>
                      {(startHint || finishHint) && (
                        <div style={{ color: 'var(--accent-text)', fontSize: 12.5, marginTop: 6, whiteSpace: 'nowrap' }}>
                          {startHint || finishHint}
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* PREDECESSORS */}
                {sectionHead('Predecessors')}
                <div style={atFieldGroup}>
                  <PredecessorSection
                    currentTask={null}
                    tasks={tasks}
                    editingTaskId={null}
                    inputStyle={atInputStyle}
                    fieldGroup={{ marginBottom: 0 }}
                    mode="local"
                    localPredecessors={pendingPredecessors}
                    onLocalChange={setPendingPredecessors}
                  />
                </div>

                {/* SOV LINKS */}
                {sectionHead('SOV Links')}
                <div style={atFieldGroup}>
                  <TaskSOVLinks
                    mode="local"
                    projectId={projectId}
                    localLinks={pendingSovLinks}
                    onLocalChange={setPendingSovLinks}
                  />
                </div>

                {/* OPTIONS */}
                {sectionHead('Options')}
                <div style={{ display: 'flex', gap: 28, ...atFieldGroup }}>
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 14.5, color: 'var(--text-1)', cursor: 'pointer' }}>
                    <input type="checkbox" name="client_visible" checked={formData.client_visible} onChange={handleFormChange}
                      style={{ width: 16, height: 16, accentColor: 'var(--accent)', cursor: 'pointer' }} />
                    Client Visible
                  </label>
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 14.5, color: 'var(--text-1)', cursor: 'pointer' }}>
                    <input type="checkbox" name="milestone_flag" checked={formData.milestone_flag} onChange={handleFormChange}
                      style={{ width: 16, height: 16, accentColor: 'var(--accent)', cursor: 'pointer' }} />
                    Milestone
                  </label>
                </div>

                {/* NOTES */}
                {sectionHead('Notes')}
                <textarea name="notes" value={formData.notes} onChange={handleFormChange}
                  rows="2" placeholder="Add notes for the field team or PM…"
                  style={atTextareaStyle}
                  onFocus={handleFocus} onBlur={handleBlur} />
                <div style={{ height: 8 }} />
              </div>
              {/* Footer */}
              <div style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '14px 20px', borderTop: '1px solid var(--border-subtle)', background: 'var(--bg-surface-2)', flexShrink: 0,
              }}>
                <div style={{ flex: 1 }} />
                <button type="button"
                  onClick={closeModal}
                  disabled={creatingTask}
                  style={{
                    padding: '0 14px', height: 36, borderRadius: 8, fontSize: 14.5, fontWeight: 500,
                    color: 'var(--text-2)', background: 'var(--bg-surface)', border: '1px solid var(--border-default)',
                    cursor: creatingTask ? 'not-allowed' : 'pointer', opacity: creatingTask ? 0.6 : 1,
                  }}
                >Cancel</button>
                <button type="button"
                  onClick={() => handleAddTask()}
                  disabled={creatingTask}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 8,
                    padding: '0 16px', height: 36, borderRadius: 8, fontSize: 14.5, fontWeight: 500, color: '#fff',
                    background: 'var(--accent)', border: '1px solid color-mix(in oklch, var(--accent) 50%, black)',
                    boxShadow: '0 1px 0 color-mix(in oklch, var(--accent) 35%, black), 0 1px 3px oklch(0.3 0.05 135 / 0.18)',
                    cursor: creatingTask ? 'wait' : 'pointer', opacity: creatingTask ? 0.8 : 1,
                  }}
                >
                  {creatingTask && (
                    <span style={{
                      width: 14, height: 14, border: '2px solid rgba(255,255,255,0.4)',
                      borderTopColor: '#fff', borderRadius: '50%',
                      animation: 'spin 0.7s linear infinite', display: 'inline-block',
                    }} />
                  )}
                  {creatingTask ? 'Creating…' : 'Create Task'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Task Properties Panel */}
      {editingTaskId && (() => {
        const currentTask = tasks.find(t => t.id === editingTaskId);
        const taskAllocs = taskAllocations[editingTaskId] || [];
        const pct = currentTask?.percent_complete || 0;

        // Shared input style
        const inputStyle = {
          width: '100%', padding: '8px 12px', fontSize: '14.5px',
          border: `1px solid ${t.cardBorder}`, borderRadius: '8px', outline: 'none',
          transition: 'border-color 0.2s', backgroundColor: t.bg,
          boxSizing: 'border-box',
        };
        const inputDisabledStyle = { ...inputStyle, backgroundColor: t.bgAlt, color: t.textSecondary, cursor: 'default' };
        const labelStyle = { display: 'block', fontSize: '12.5px', fontWeight: 600, color: t.textSecondary, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' };
        const sectionTitleStyle = { fontSize: '13.5px', fontWeight: 700, color: t.text, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '12px', paddingBottom: '8px', borderBottom: '1px solid var(--border-subtle)' };
        const fieldGroup = { marginBottom: '14px' };

        return (
          <div className="modal-overlay" onClick={() => setEditingTaskId(null)}>
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                background: t.bg, borderRadius: '16px', width: '580px', maxHeight: '88vh',
                overflow: 'hidden', display: 'flex', flexDirection: 'column',
                boxShadow: '0 25px 60px rgba(0,0,0,0.2), 0 0 0 1px rgba(0,0,0,0.05)',
                animation: 'slideUp 0.25s ease',
              }}
            >
              {/* Header */}
              <div style={{
                padding: '20px 24px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
                borderBottom: '1px solid var(--border-subtle)',
              }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
                    <span style={{
                      ...getTaskTypeStyle(editFormData.task_type),
                      padding: '3px 10px', borderRadius: '12px', fontSize: '12.5px', fontWeight: 600,
                    }}>
                      {editFormData.task_type}
                    </span>
                    <span style={{ fontSize: '13.5px', color: t.textMuted, fontWeight: 500 }}>{editFormData.task_code}</span>
                    <span style={{
                      ...getStatusStyle(editFormData.status),
                      padding: '3px 10px', borderRadius: '12px', fontSize: '12.5px', fontWeight: 500,
                    }}>
                      {editFormData.status || 'Not Started'}
                    </span>
                  </div>
                  <h2 style={{ margin: 0, fontSize: '19.5px', fontWeight: 600, color: t.heading }}>
                    {editFormData.task_name || 'Task Properties'}
                  </h2>
                </div>
                <button
                  onClick={() => setEditingTaskId(null)}
                  style={{
                    background: 'none', border: 'none', cursor: 'pointer', padding: '4px',
                    color: t.textMuted, fontSize: '21.5px', lineHeight: 1, borderRadius: '6px',
                    transition: 'all 0.15s',
                  }}
                  onMouseEnter={(e) => { e.target.style.backgroundColor = t.dark ? 'rgba(255,255,255,0.1)' : '#f1f5f9'; e.target.style.color = t.heading; }}
                  onMouseLeave={(e) => { e.target.style.backgroundColor = 'transparent'; e.target.style.color = t.textMuted; }}
                >
                  &times;
                </button>
              </div>

              {/* Scrollable body */}
              <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>

                {/* General section */}
                <div style={sectionTitleStyle}>General</div>
                <div style={fieldGroup}>
                  <label style={labelStyle}>Task Name</label>
                  <input type="text" name="task_name" value={editFormData.task_name} onChange={handleEditFormChange} style={inputStyle} onFocus={(e) => e.target.style.borderColor = '#3b82f6'} onBlur={(e) => e.target.style.borderColor = t.cardBorder} />
                </div>
                <div style={fieldGroup}>
                  <label style={labelStyle}>Description</label>
                  <textarea name="task_description" value={editFormData.task_description} onChange={handleEditFormChange} rows="2" style={{ ...inputStyle, resize: 'vertical', minHeight: '60px' }} onFocus={(e) => e.target.style.borderColor = '#3b82f6'} onBlur={(e) => e.target.style.borderColor = t.cardBorder} />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', ...fieldGroup }}>
                  <div>
                    <label style={labelStyle}>Building Area / Phase</label>
                    <input type="text" name="building_area_phase" value={editFormData.building_area_phase} onChange={handleEditFormChange} style={inputStyle} onFocus={(e) => e.target.style.borderColor = '#3b82f6'} onBlur={(e) => e.target.style.borderColor = t.cardBorder} />
                  </div>
                  <div>
                    <label style={labelStyle}>Task Type</label>
                    <select name="task_type" value={editFormData.task_type} onChange={handleEditFormChange} style={inputStyle}>
                      <option>Labor</option>
                      <option>Material</option>
                      <option>Procurement</option>
                      <option>Engineering</option>
                      <option>Inspection</option>
                      <option>Milestone</option>
                    </select>
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', ...fieldGroup }}>
                  <div>
                    <label style={labelStyle}>Status</label>
                    <select name="status" value={editFormData.status || 'Not Started'} onChange={handleEditFormChange} style={inputStyle}>
                      <option>Not Started</option>
                      <option>In Progress</option>
                      <option>Complete</option>
                    </select>
                  </div>
                  <div>
                    <label style={labelStyle}>Responsible Party</label>
                    <input type="text" name="responsible_party" value={editFormData.responsible_party} onChange={handleEditFormChange} style={inputStyle} onFocus={(e) => e.target.style.borderColor = '#3b82f6'} onBlur={(e) => e.target.style.borderColor = t.cardBorder} />
                  </div>
                </div>

                {/* Schedule section */}
                <div style={{ ...sectionTitleStyle, marginTop: '8px' }}>Schedule</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 100px', gap: '12px', ...fieldGroup }}>
                  <div>
                    <label style={labelStyle}>Start Date</label>
                    <input type="date" name="start_date" value={editFormData.start_date} onChange={handleEditFormChange} style={inputStyle} onFocus={(e) => e.target.style.borderColor = '#3b82f6'} onBlur={(e) => e.target.style.borderColor = t.cardBorder} />
                  </div>
                  <div>
                    <label style={labelStyle}>Finish Date</label>
                    <input type="date" name="finish_date" value={editFormData.finish_date} onChange={handleEditFormChange} style={inputStyle} onFocus={(e) => e.target.style.borderColor = '#3b82f6'} onBlur={(e) => e.target.style.borderColor = t.cardBorder} />
                  </div>
                  <div>
                    <label style={labelStyle}>Duration</label>
                    <input type="text" value={`${editDuration} days`} disabled style={inputDisabledStyle} />
                  </div>
                </div>

                {/* Progress slider */}
                {(() => {
                  const maxPct = getMaxProgress(currentTask);
                  // Round max down to nearest step of 5 so the slider aligns to step increments
                  const sliderMax = Math.floor(maxPct / 5) * 5;
                  return (
                    <div style={{ ...fieldGroup }}>
                      <label style={labelStyle}>
                        Progress — {pct}%
                        {maxPct < 100 && <span style={{ color: '#f59e0b', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}> (max {sliderMax}% — {latestDraftPeriodEnd ? `schedule prorated to billing period end (${latestDraftPeriodEnd})` : 'schedule prorated to today'})</span>}
                      </label>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{ flex: 1, position: 'relative' }}>
                          <input
                            type="range"
                            min="0"
                            max={sliderMax}
                            step="5"
                            value={Math.min(pct, sliderMax)}
                            onChange={(e) => {
                              const newPct = Math.min(parseInt(e.target.value, 10), sliderMax);
                              handlePercentCompleteChange(editingTaskId, newPct);
                            }}
                            style={{
                              width: '100%', height: '8px', cursor: sliderMax > 0 ? 'pointer' : 'not-allowed',
                              accentColor: pct >= 100 ? '#22c55e' : pct >= 50 ? '#3b82f6' : '#94a3b8',
                            }}
                            disabled={sliderMax === 0}
                          />
                          {maxPct < 100 && (
                            <div style={{
                              position: 'absolute', top: '-2px', height: '12px', right: 0,
                              width: `${100 - maxPct}%`, background: 'repeating-linear-gradient(45deg, transparent, transparent 2px, rgba(0,0,0,0.08) 2px, rgba(0,0,0,0.08) 4px)',
                              borderRadius: '0 4px 4px 0', pointerEvents: 'none',
                            }} />
                          )}
                        </div>
                        <span style={{ fontSize: '14.5px', fontWeight: 600, color: t.text, minWidth: '40px', textAlign: 'right' }}>{pct}%</span>
                      </div>
                    </div>
                  );
                })()}

                {/* Predecessors section */}
                <div style={{ ...sectionTitleStyle, marginTop: '8px' }}>Predecessors</div>
                <PredecessorSection
                  currentTask={currentTask}
                  tasks={tasks}
                  editingTaskId={editingTaskId}
                  inputStyle={inputStyle}
                  fieldGroup={fieldGroup}
                  addPredecessor={addPredecessor}
                  updatePredecessor={updatePredecessor}
                  removePredecessor={removePredecessor}
                  fetchTasks={fetchTasks}
                  confirm={confirm}
                />

                {/* Dependent Tasks (successors) section */}
                {(() => {
                  const successors = tasks.filter(t =>
                    (t.predecessors || []).some(p => p.predecessor_task_id != null && Number(p.predecessor_task_id) === Number(editingTaskId))
                  );
                  return (
                    <div>
                      <div style={{ ...sectionTitleStyle, marginTop: '8px' }}>Dependent Tasks</div>
                      {successors.length === 0 ? (
                        <div style={{ fontSize: '14.5px', color: t.textMuted, padding: '8px 0' }}>
                          No tasks depend on this task
                        </div>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', padding: '4px 0' }}>
                          {successors.map(s => {
                            const pred = (s.predecessors || []).find(p => Number(p.predecessor_task_id) === Number(editingTaskId));
                            const depLabel = DEP_TYPES.find(d => d.value === pred?.dependency_type)?.short || pred?.dependency_type || 'FS';
                            const lagText = pred?.lag_days ? ` +${pred.lag_days}d` : '';
                            return (
                              <div key={s.id} style={{
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                padding: '8px 10px', backgroundColor: t.bgAlt, borderRadius: '6px',
                                border: `1px solid ${t.cardBorder}`, fontSize: '14.5px',
                              }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, minWidth: 0 }}>
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#8b5cf6" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                                    <line x1="5" y1="12" x2="19" y2="12" />
                                    <polyline points="12 5 19 12 12 19" />
                                  </svg>
                                  <div style={{ minWidth: 0 }}>
                                    <div>
                                      <span style={{ fontWeight: 500, color: t.text }}>{s.task_code}</span>
                                      <span style={{ color: t.textSecondary, marginLeft: '6px' }}>{s.task_name}</span>
                                    </div>
                                    {(s.start_date || s.finish_date) && (
                                      <div style={{ fontSize: '12.5px', color: t.textMuted, marginTop: '2px' }}>
                                        {s.start_date ? `Start: ${fmtDate(s.start_date)}` : ''}
                                        {s.start_date && s.finish_date ? '  —  ' : ''}
                                        {s.finish_date ? `Finish: ${fmtDate(s.finish_date)}` : ''}
                                      </div>
                                    )}
                                  </div>
                                </div>
                                <span style={{
                                  fontSize: '12.5px', fontWeight: 600, color: '#8b5cf6',
                                  backgroundColor: '#f5f3ff', padding: '2px 8px', borderRadius: '10px',
                                }}>
                                  {depLabel}{lagText}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* Options section */}
                <div style={{ ...sectionTitleStyle, marginTop: '8px' }}>Options</div>
                <div style={{ display: 'flex', gap: '24px', ...fieldGroup }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14.5px', color: t.text, cursor: 'pointer' }}>
                    <input type="checkbox" name="client_visible" checked={editFormData.client_visible} onChange={handleEditFormChange}
                      style={{ width: '16px', height: '16px', accentColor: '#3b82f6', cursor: 'pointer' }} />
                    Client Visible
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14.5px', color: t.text, cursor: 'pointer' }}>
                    <input type="checkbox" name="milestone_flag" checked={editFormData.milestone_flag} onChange={handleEditFormChange}
                      style={{ width: '16px', height: '16px', accentColor: '#3b82f6', cursor: 'pointer' }} />
                    Milestone
                  </label>
                </div>

                {/* SOV Links preview */}
                {taskAllocs.length > 0 && (
                  <>
                    <div style={{ ...sectionTitleStyle, marginTop: '8px' }}>SOV Links</div>
                    <div style={{ ...fieldGroup }}>
                      {taskAllocs.map(a => {
                        const contractAmt = parseFloat(a.sov_contract_amount) || 0;
                        const pct = contractAmt > 0 ? Math.round((parseFloat(a.allocation_amount) / contractAmt) * 100) : 0;
                        return (
                          <div key={a.id} style={{
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                            padding: '8px 12px', backgroundColor: t.bgAlt, borderRadius: '8px',
                            marginBottom: '6px', fontSize: '13.5px',
                          }}>
                            <div>
                              <span style={{ fontWeight: 600, color: t.text }}>{a.sov_code}</span>
                              <span style={{ color: t.textSecondary, marginLeft: '8px' }}>{a.sov_line_description}</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <span style={{ fontWeight: 600, color: t.text }}>
                                ${(a.allocation_amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                              </span>
                              <span style={{
                                fontSize: '12.5px', fontWeight: 600, color: '#3b82f6',
                                backgroundColor: '#eff6ff', padding: '2px 8px', borderRadius: '10px',
                              }}>
                                {pct}%
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}

                {/* Notes section */}
                <div style={{ ...sectionTitleStyle, marginTop: '8px' }}>Notes</div>
                <div style={fieldGroup}>
                  <textarea name="notes" value={editFormData.notes} onChange={handleEditFormChange} rows="3" style={{ ...inputStyle, resize: 'vertical', minHeight: '70px' }} placeholder="Add notes about this task..." onFocus={(e) => e.target.style.borderColor = '#3b82f6'} onBlur={(e) => e.target.style.borderColor = t.cardBorder} />
                </div>

              </div>
              {/* Footer — pinned outside scrollable area */}
              <div style={{
                display: 'flex', alignItems: 'center', gap: '8px',
                padding: '16px 24px', borderTop: '1px solid var(--border-subtle)',
                flexShrink: 0,
              }}>
                <button type="button"
                  onClick={() => { handleDeleteTask(editingTaskId); }}
                  disabled={deletingTaskId === editingTaskId || savingEdit}
                  style={{
                    background: 'none', border: '1px solid #fecaca', color: '#dc2626',
                    padding: '8px 16px', borderRadius: '8px', fontSize: '14.5px', fontWeight: 500,
                    cursor: deletingTaskId === editingTaskId ? 'wait' : (savingEdit ? 'not-allowed' : 'pointer'),
                    opacity: (deletingTaskId === editingTaskId || savingEdit) ? 0.7 : 1,
                    transition: 'all 0.15s',
                    display: 'inline-flex', alignItems: 'center', gap: 8,
                  }}
                  onMouseEnter={(e) => { if (deletingTaskId !== editingTaskId && !savingEdit) e.currentTarget.style.backgroundColor = '#fef2f2'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                >
                  {deletingTaskId === editingTaskId && (
                    <span style={{
                      width: 14, height: 14, border: '2px solid rgba(0,0,0,0.1)',
                      borderTopColor: '#dc2626', borderRadius: '50%',
                      animation: 'spin 0.7s linear infinite', display: 'inline-block',
                    }} />
                  )}
                  {deletingTaskId === editingTaskId ? 'Deleting…' : 'Delete'}
                </button>
                <div style={{ flex: 1 }} />
                <button type="button" onClick={() => setEditingTaskId(null)}
                  disabled={savingEdit || deletingTaskId === editingTaskId}
                  style={{
                    background: t.bgAlt, border: 'none', color: t.textSecondary,
                    padding: '8px 20px', borderRadius: '8px', fontSize: '14.5px', fontWeight: 500,
                    cursor: (savingEdit || deletingTaskId === editingTaskId) ? 'not-allowed' : 'pointer',
                    opacity: (savingEdit || deletingTaskId === editingTaskId) ? 0.6 : 1,
                    transition: 'all 0.15s',
                  }}
                  onMouseEnter={(e) => { if (!savingEdit && deletingTaskId !== editingTaskId) e.currentTarget.style.backgroundColor = t.dark ? '#2d3748' : '#e2e8f0'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = t.bgAlt; }}
                >
                  Cancel
                </button>
                <button type="button"
                  onClick={() => handleSaveEdit()}
                  disabled={savingEdit || deletingTaskId === editingTaskId}
                  style={{
                    background: '#3b82f6', border: 'none', color: '#fff',
                    padding: '8px 24px', borderRadius: '8px', fontSize: '14.5px', fontWeight: 600,
                    cursor: savingEdit ? 'wait' : (deletingTaskId === editingTaskId ? 'not-allowed' : 'pointer'),
                    opacity: (savingEdit || deletingTaskId === editingTaskId) ? 0.8 : 1,
                    transition: 'all 0.15s',
                    boxShadow: '0 1px 3px rgba(59,130,246,0.3)',
                    display: 'inline-flex', alignItems: 'center', gap: 8,
                  }}
                  onMouseEnter={(e) => { if (!savingEdit && deletingTaskId !== editingTaskId) e.currentTarget.style.backgroundColor = '#2563eb'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#3b82f6'; }}
                >
                  {savingEdit && (
                    <span style={{
                      width: 14, height: 14, border: '2px solid rgba(255,255,255,0.4)',
                      borderTopColor: '#fff', borderRadius: '50%',
                      animation: 'spin 0.7s linear infinite', display: 'inline-block',
                    }} />
                  )}
                  {savingEdit ? 'Saving…' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      <ConfirmDialog {...confirmDialogProps} />

      {/* ═══ Schedule Filter popover ═══
          Three grouped checkbox sections (AND-combined): Section,
          Status, Type. Toggles persist into sharedSchedule.scheduleFilter
          so both the Tasks panel AND the Gantt panel re-filter in
          lockstep. Each group has its own Clear link. */}
      <Popover
        open={filterMenuOpen}
        anchor={filterMenuAnchor}
        onClose={() => setFilterMenuOpen(false)}
        minWidth={250}
      >
        {(() => {
          const f = sharedSchedule?.scheduleFilter
            || { sections: new Set(), statuses: new Set(), types: new Set() };
          const setF = sharedSchedule?.setScheduleFilter || (() => {});
          const toggleIn = (dim, value) => {
            setF((prev) => {
              const nextDim = new Set(prev[dim]);
              if (nextDim.has(value)) nextDim.delete(value);
              else nextDim.add(value);
              return { ...prev, [dim]: nextDim };
            });
          };
          const clearDim = (dim) => setF((prev) => ({ ...prev, [dim]: new Set() }));

          // Section choices = sections actually present on this
          // project. Pulled from the canonical (unfiltered) sections
          // list so a user can still re-add a section they just
          // filtered out. Drag-and-drop section reordering happens
          // on the same canonical list, so the order here matches.
          const sectionChoices = (sharedSchedule?.sections || sections);

          return (
            <>
              {/* SECTION group */}
              <PopoverHeader
                title="Section"
                onClear={f.sections.size > 0 ? () => clearDim('sections') : null}
              />
              {sectionChoices.length === 0 ? (
                <div style={{ padding: '6px 10px', fontSize: 13.5, color: 'var(--text-4)' }}>
                  No sections defined.
                </div>
              ) : (
                sectionChoices.map((s) => (
                  <CheckMenuItem
                    key={`sec-${s}`}
                    checked={f.sections.has(s)}
                    label={s}
                    onClick={() => toggleIn('sections', s)}
                  />
                ))
              )}

              <PopoverDivider />

              {/* STATUS group */}
              <PopoverHeader
                title="Status"
                onClear={f.statuses.size > 0 ? () => clearDim('statuses') : null}
              />
              {['Not Started', 'In Progress', 'Complete'].map((s) => (
                <CheckMenuItem
                  key={`st-${s}`}
                  checked={f.statuses.has(s)}
                  label={s}
                  onClick={() => toggleIn('statuses', s)}
                />
              ))}

              <PopoverDivider />

              {/* TYPE group */}
              <PopoverHeader
                title="Type"
                onClear={f.types.size > 0 ? () => clearDim('types') : null}
              />
              {SCHEDULE_TASK_TYPES.map((tt) => (
                <CheckMenuItem
                  key={`tp-${tt}`}
                  checked={f.types.has(tt)}
                  label={tt}
                  onClick={() => toggleIn('types', tt)}
                />
              ))}
            </>
          );
        })()}
      </Popover>

      {/* ═══ Schedule Sort popover ═══
          Single-select radio list. Sort key flows up via
          sharedSchedule.setScheduleSort so the Gantt sees the same
          ordering. Within-section only — cross-section order remains
          AIA-grouped via the sections list. */}
      <Popover
        open={sortMenuOpen}
        anchor={sortMenuAnchor}
        onClose={() => setSortMenuOpen(false)}
        minWidth={250}
      >
        {(() => {
          const sortKey = sharedSchedule?.scheduleSort || 'default';
          const isCustomSort = sortKey !== 'default';
          return (
            <>
              <PopoverHeader
                title="Sort by"
                onClear={isCustomSort
                  ? () => {
                      sharedSchedule?.setScheduleSort?.('default');
                      setSortMenuOpen(false);
                    }
                  : null
                }
              />
              {SCHEDULE_SORT_OPTIONS.map((opt) => (
                <RadioMenuItem
                  key={opt.key}
                  selected={sortKey === opt.key}
                  label={opt.label}
                  onClick={() => {
                    sharedSchedule?.setScheduleSort?.(opt.key);
                    setSortMenuOpen(false);
                  }}
                />
              ))}
              {isCustomSort && (
                <>
                  <PopoverDivider />
                  <button
                    type="button"
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => {
                      e.stopPropagation();
                      sharedSchedule?.setScheduleSort?.('default');
                      setSortMenuOpen(false);
                    }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 8,
                      width: '100%', padding: '7px 10px',
                      border: 'none',
                      background: 'transparent',
                      color: 'var(--accent-text)',
                      cursor: 'pointer',
                      borderRadius: 5,
                      textAlign: 'left',
                      fontSize: 14, fontWeight: 500,
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-hover)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                  >
                    Reset to default order
                  </button>
                </>
              )}
            </>
          );
        })()}
      </Popover>
    </div>
  );
};

export default TasksTab;
